import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../firebase-client';
import { useSupportAdminAccess } from '../../hooks/useSupportAdminAccess';
import {
  createBoardReply,
  createBoardTopic,
  deleteBoard,
  deleteMyBoardContent,
  ensureBoardModerationCase,
  getBoardModerationEvidence,
  listAccessibleBoards,
  listVisibleEventCommentTopics,
  openBoardModerationAdminThread,
  reportBoardContent,
  reviewBoardModerationCase,
  searchBoardModerationCases,
  searchBoardMentionTargets,
  setBoardTopicPinned,
  updateBoardReply,
  updateBoardTopic,
} from '../../utils/boardApi';
import { boardHtmlToText, sanitizeBoardHtml } from '../../utils/boardModeration';
import { GIPHY_RATINGS } from '../../utils/giphy';
import {
  discardMediaUpload,
  uploadModeratedMedia,
  validateMediaAttachmentFiles,
} from '../../utils/mediaUploads';
import {
  normalizeBoardGifSettings,
  normalizeTeamBoardGifSettings,
  resolveBoardGifPolicy,
} from '../../utils/gifSettings';
import { normalizeMemberTags } from '../../utils/memberTags';
import { safeOnSnapshot } from '../../utils/firestoreSubscriptions';
import { boardNotificationKey, isSelectedBoardNotification, normalizeMessageNotification } from '../../utils/messageNotificationUtils';
import RichTextEditor from './boards/RichTextEditor';
import BoardRichContent from './BoardRichContent';
import BoardReactionBar from './BoardReactionBar';
import ComposerMediaRail from './ComposerMediaRail';
import MediaAttachmentGrid from './MediaAttachmentGrid';
import { usePwaInstallPrompt } from '../pwa/PwaInstallContext';
import { useCompactPwaViewport } from '../../hooks/useCompactPwaViewport';
import { readOfflineContentCache, writeOfflineContentCache } from '../../utils/offlineContentCache';
import { useOnlineRefreshSignal } from '../../utils/onlineStatus';
import DestructiveActionDialog from '../ui/DestructiveActionDialog';

const CACHE_SCOPE = 'boards';
const MEDIA_BUSY_STATUSES = new Set(['preparing', 'uploading', 'awaiting_upload', 'scanning']);

function isPermissionDeniedError(error) {
  const code = String(error?.code || '').trim().toLowerCase();
  return code === 'permission-denied' || code === 'firestore/permission-denied';
}

function mediaDraftItemId(file, index) {
  return `${Date.now()}-${index}-${file.name}-${file.size}`;
}

function mediaDraftStatusDetail(status, progress) {
  if (status === 'uploading') return `${progress}% complete`;
  if (status === 'preparing') return 'Preparing upload';
  if (status === 'error') return 'Could not attach this media';
  return 'Finalising upload';
}

function isMediaUploadAlreadyUsedError(error) {
  return /media upload .*already been used/i.test(error?.message || '');
}

function timeValue(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function formatDateTime(value) {
  const ts = value?.toDate?.() || value || null;
  if (!ts) return '—';
  const date = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function formatClockTime(date) {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function isSameCalendarDay(left, right) {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

function formatRelevantDateTime(value) {
  const ts = value?.toDate?.() || value || null;
  if (!ts) return '—';
  const date = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const absDiffMs = Math.abs(diffMs);
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;

  if (absDiffMs < minuteMs) return 'just now';
  if (isSameCalendarDay(date, now) && absDiffMs < hourMs) {
    const minutes = Math.max(1, Math.round(absDiffMs / minuteMs));
    return diffMs >= 0 ? `${minutes} minute${minutes === 1 ? '' : 's'} ago` : `in ${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  if (isSameCalendarDay(date, now)) {
    const hours = Math.max(1, Math.round(absDiffMs / hourMs));
    return diffMs >= 0 ? `${hours} hour${hours === 1 ? '' : 's'} ago` : `in ${hours} hour${hours === 1 ? '' : 's'}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDay(date, yesterday)) {
    return `yesterday @ ${formatClockTime(date)}`;
  }

  return `${date.toLocaleDateString([], { day: '2-digit', month: 'short' })} @ ${formatClockTime(date)}`;
}

function isElementOnScreen(element) {
  if (!element || typeof element.getBoundingClientRect !== 'function') return false;
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  if (rect.width <= 0 || rect.height <= 0 || viewportHeight <= 0 || viewportWidth <= 0) return false;
  return rect.bottom > 24
    && rect.top < viewportHeight - 24
    && rect.right > 0
    && rect.left < viewportWidth;
}

function sortByActivity(items = [], field = 'lastActivityAt') {
  return [...items].sort((left, right) => {
    const diff = timeValue(right?.[field]) - timeValue(left?.[field]);
    if (diff !== 0) return diff;
    return String(left?.name || left?.id || '').localeCompare(String(right?.name || right?.id || ''));
  });
}

function extractTeamTagOptions(tags = []) {
  return (Array.isArray(tags) ? tags : [])
    .filter((tag) => tag?.type !== 'ageRule')
    .map((tag) => String(tag?.name || '').trim())
    .filter(Boolean);
}

function getBoardVisibilityLabel(board) {
  if (board?.systemType === 'event_comments') return 'Event comments';
  return board?.visibility === 'tags'
    ? `${(board?.allowedTagNames || []).length || 0} tags`
    : 'Open to all';
}

function isEventCommentsBoard(board) {
  return board?.systemType === 'event_comments' || board?.id === 'events';
}

function isDiscussionBoard(board) {
  return !isEventCommentsBoard(board);
}

function clampPreview(value = '', max = 140) {
  const text = String(value || '').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 3)}...`;
}

function getTopicMediaAttachments(topic = {}) {
  return [
    ...(topic?.media ? [topic.media] : []),
    ...(Array.isArray(topic?.mediaAttachments) ? topic.mediaAttachments : []),
  ].filter(Boolean);
}

function getTopicTitle(topic) {
  if (topic?.kind === 'event_comment') {
    return topic.eventName || 'Event comments';
  }
  const explicitTitle = String(topic?.title || '').replace(/\s+/g, ' ').trim();
  if (explicitTitle) return clampPreview(explicitTitle, 84);
  const text = String(topic?.bodyText || '').replace(/\s+/g, ' ').trim();
  if (!text && getTopicMediaAttachments(topic).length > 0) return 'Media topic';
  if (!text) return 'Untitled topic';
  const firstSentence = text.split(/(?<=[.!?])\s+/)[0]?.trim() || '';
  const candidate = firstSentence || text;
  return clampPreview(candidate, 84);
}

function getTopicExcerpt(topic) {
  if (topic?.kind === 'event_comment') {
    const eventWhen = formatDateTime(topic.eventStart);
    const preview = String(topic?.bodyText || '').replace(/\s+/g, ' ').trim();
    return clampPreview([eventWhen !== '—' ? eventWhen : '', preview].filter(Boolean).join(' · '), 180);
  }
  const text = String(topic?.bodyText || '').replace(/\s+/g, ' ').trim();
  if (!text) {
    const mediaCount = getTopicMediaAttachments(topic).length;
    return mediaCount > 0 ? `${mediaCount} media attachment${mediaCount === 1 ? '' : 's'}` : '';
  }
  const title = String(topic?.title || '').trim() ? '' : getTopicTitle(topic);
  const remainder = title && text.startsWith(title) ? text.slice(title.length).trim() : text;
  return clampPreview(remainder || text, 180);
}

function escapeHtml(value = '') {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getReplyPrefill(reply) {
  const authorName = String(reply?.authorName || 'Member').trim();
  const replyText = clampPreview(boardHtmlToText(reply?.bodyHtml || reply?.bodyText || ''), 140);
  const quote = replyText ? `<blockquote><p>${escapeHtml(replyText)}</p></blockquote>` : '';
  return `<p><strong>@${escapeHtml(authorName)}</strong> </p>${quote}<p></p>`;
}

function mergeDocMaps(left = [], right = []) {
  const byId = new Map();
  [...left, ...right].forEach((entry) => {
    if (!entry?.id) return;
    byId.set(entry.key || entry.id, entry);
  });
  return Array.from(byId.values());
}

function sortModerationCases(cases = []) {
  return [...cases].sort((left, right) => (
    timeValue(right?.updatedAt || right?.createdAt) - timeValue(left?.updatedAt || left?.createdAt)
  ));
}

const TOPIC_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'pinned', label: 'Pinned' },
  { id: 'mine', label: 'Mine' },
  { id: 'unanswered', label: 'Unanswered' },
  { id: 'flagged', label: 'Flagged', adminOnly: true },
];

const EMPTY_CASE_FILTERS = {
  source: '',
  contentType: '',
  action: '',
  teamId: '',
  boardId: '',
};

const CASE_SOURCE_OPTIONS = [
  { id: 'auto', label: 'Auto' },
  { id: 'member_report', label: 'Member report' },
  { id: 'member_self_delete', label: 'Member delete' },
  { id: 'auto_and_member_report', label: 'Auto + report' },
  { id: 'admin_review', label: 'Admin review' },
];

const CASE_CONTENT_OPTIONS = [
  { id: 'topic', label: 'Topic' },
  { id: 'reply', label: 'Reply' },
];

const CASE_ACTION_OPTIONS = [
  { id: 'left_visible', label: 'Left visible' },
  { id: 'hide', label: 'Hidden' },
  { id: 'delete_content', label: 'Deleted' },
  { id: 'member_self_delete', label: 'Member deleted' },
  { id: 'lock_topic', label: 'Locked' },
  { id: 'unlock_topic', label: 'Unlocked' },
  { id: 'mute_7d', label: 'Muted 7d' },
  { id: 'mute_30d', label: 'Muted 30d' },
  { id: 'unmute', label: 'Unmuted' },
  { id: 'revoke_board_access', label: 'Access removed' },
  { id: 'restore_board_access', label: 'Access restored' },
  { id: 'accept_club_action', label: 'Accepted club action' },
  { id: 'override_club_action', label: 'Overrode club action' },
];

function normalizeIds(value) {
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(
    value
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter(Boolean)
  ));
}

function areSameStringList(left = [], right = []) {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

function normalizePinnedTopicIdsByBoard(value) {
  if (!value || typeof value !== 'object') return {};
  return Object.entries(value).reduce((acc, [boardId, topicIds]) => {
    const normalizedBoardId = typeof boardId === 'string' ? boardId.trim() : '';
    if (!normalizedBoardId) return acc;
    acc[normalizedBoardId] = normalizeIds(topicIds);
    return acc;
  }, {});
}

function getTopicSearchText(topic = {}) {
  return [
    getTopicTitle(topic),
    getTopicExcerpt(topic),
    topic.bodyText || '',
    topic.authorName || '',
  ].join(' ').toLowerCase();
}

function getAuthorName(user) {
  if (!user) return 'You';
  return user.displayName || user.email || 'You';
}

export function getBoardsMobileStep({ isMobile, isCompactPwaViewport = false, selectedTopicId = '', selectedBoard = null } = {}) {
  if (!isMobile && !isCompactPwaViewport) return 'desktop';
  if (selectedTopicId) return 'thread';
  if (selectedBoard) return 'topics';
  return 'browse';
}

export function shouldSubscribeToSupportCases({ isSupportAdmin = false, panel = '' } = {}) {
  return Boolean(isSupportAdmin && panel === 'support');
}

function buildPendingTopic({ title, bodyHtml, user, boardId, mediaAttachments = [] }) {
  const now = new Date();
  return {
    id: `pending-topic-${now.getTime()}`,
    boardId,
    authorId: user?.uid || '',
    authorName: getAuthorName(user),
    title,
    bodyHtml,
    bodyText: boardHtmlToText(bodyHtml),
    ...(Array.isArray(mediaAttachments) && mediaAttachments.length > 0 ? { mediaAttachments } : {}),
    createdAt: now,
    updatedAt: now,
    lastReplyAt: now,
    moderationStatus: 'visible',
    flagStatus: 'clear',
    replyCount: 0,
    pending: true,
  };
}

function buildPendingReply({ bodyHtml, user, topicId, media = null, mediaAttachments = [] }) {
  const now = new Date();
  return {
    id: `pending-reply-${now.getTime()}`,
    topicId,
    authorId: user?.uid || '',
    authorName: getAuthorName(user),
    bodyHtml,
    bodyText: boardHtmlToText(bodyHtml),
    ...(media ? { media } : {}),
    ...(Array.isArray(mediaAttachments) && mediaAttachments.length > 0 ? { mediaAttachments } : {}),
    createdAt: now,
    updatedAt: now,
    moderationStatus: 'visible',
    flagStatus: 'clear',
    pending: true,
  };
}

const EMPTY_MENTION_METADATA = {
  mentions: [],
  mentionsBoard: false,
  mentionTags: [],
};

function normalizeMentionMetadata(value = EMPTY_MENTION_METADATA) {
  const mentionsByUid = new Map();
  const mentionTagsByName = new Map();
  (Array.isArray(value?.mentions) ? value.mentions : []).forEach((mention) => {
    const uid = typeof mention?.uid === 'string' ? mention.uid.trim() : '';
    if (!uid) return;
    mentionsByUid.set(uid, {
      uid,
      label: typeof mention?.label === 'string' && mention.label.trim() ? mention.label.trim() : 'Member',
    });
  });
  (Array.isArray(value?.mentionTags) ? value.mentionTags : []).forEach((mentionTag) => {
    const name = typeof mentionTag?.name === 'string' ? mentionTag.name.trim() : '';
    if (!name) return;
    mentionTagsByName.set(name.toLowerCase(), {
      name,
      label: typeof mentionTag?.label === 'string' && mentionTag.label.trim() ? mentionTag.label.trim() : name,
    });
  });
  return {
    mentions: Array.from(mentionsByUid.values()),
    mentionsBoard: Boolean(value?.mentionsBoard),
    mentionTags: Array.from(mentionTagsByName.values()),
  };
}

function isPendingId(value = '') {
  return String(value).startsWith('pending-');
}

function IconActionButton({ onClick, label, children, disabled = false }) {
  return (
    <button
      type="button"
      className="board-icon-action"
      onClick={onClick}
      aria-label={label}
      title={label}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function ActionMenu({ label = 'More actions', children, trigger = '...' }) {
  const [open, setOpen] = useState(false);
  const [alignEnd, setAlignEnd] = useState(true);
  const menuRef = useRef(null);

  const updateMenuAlignment = useCallback(() => {
    const bounds = menuRef.current?.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    if (!bounds || !viewportWidth) return;
    setAlignEnd((bounds.left + bounds.width / 2) > (viewportWidth / 2));
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    updateMenuAlignment();
    const handlePointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    window.addEventListener('resize', updateMenuAlignment);
    window.addEventListener('orientationchange', updateMenuAlignment);
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', updateMenuAlignment);
      window.removeEventListener('orientationchange', updateMenuAlignment);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, updateMenuAlignment]);

  const closeOnMouseLeave = () => {
    if (window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
      setOpen(false);
    }
  };

  return (
    <div
      ref={menuRef}
      className={`board-action-menu ${open ? 'is-open' : ''} ${alignEnd ? 'is-aligned-end' : 'is-aligned-start'}`}
      onMouseLeave={closeOnMouseLeave}
    >
      <button
        type="button"
        className="board-action-menu__trigger"
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          if (!open) updateMenuAlignment();
          setOpen((current) => !current);
        }}
      >
        {trigger}
      </button>
      {open && (
      <div className="board-action-menu__panel" role="menu">
        {children}
      </div>
      )}
    </div>
  );
}

function PinActionMenu({
  topic,
  canPinForAll = false,
  savingPersonal = false,
  savingGlobal = false,
  onTogglePersonal,
  onToggleGlobal,
}) {
  if (!topic || topic.pending) return null;

  return (
    <ActionMenu label="Pin topic" trigger={<span className="board-pin-icon" aria-hidden="true" />}>
      <ActionMenuItem disabled={savingPersonal} onClick={onTogglePersonal}>
        {savingPersonal
          ? 'Saving...'
          : topic.isPersonalPinned
            ? 'Unpin for me'
            : 'Pin for me'}
      </ActionMenuItem>
      {canPinForAll && (
        <ActionMenuItem disabled={savingGlobal} onClick={onToggleGlobal}>
          {savingGlobal
            ? 'Updating...'
            : topic.isPinned
              ? 'Unpin for all'
              : 'Pin for all'}
        </ActionMenuItem>
      )}
    </ActionMenu>
  );
}

function ActionMenuItem({ children, onClick, disabled = false }) {
  return (
    <button
      type="button"
      className="board-action-menu__item"
      role="menuitem"
      disabled={disabled}
      onClick={(event) => {
        event.currentTarget.closest('.board-action-menu')?.querySelector('.board-action-menu__trigger')?.click();
        onClick?.();
      }}
    >
      {children}
    </button>
  );
}

function FlagBadge({ content }) {
  if (!content?.flagStatus || content.flagStatus === 'clear') return null;
  const label = String(content.flagStatus).replaceAll('_', ' ');
  return <span className="board-flag-badge">{label}</span>;
}

function getCaseReviewTrack(caseEntry, scope = 'club') {
  const key = scope === 'platform' ? 'platformReview' : 'clubReview';
  const legacyOpen = !caseEntry?.[key] && (!caseEntry?.status || caseEntry.status === 'open');
  return caseEntry?.[key] || {
    status: legacyOpen ? 'open' : 'resolved',
    resolution: caseEntry?.resolution || null,
    reviewedBy: caseEntry?.resolvedBy || null,
    reviewedAt: caseEntry?.resolvedAt || null,
  };
}

function isCaseOpenForScope(caseEntry, scope = 'club') {
  const track = getCaseReviewTrack(caseEntry, scope);
  if (scope === 'platform') return track.status === 'open';
  return track.status === 'open';
}

function isCaseVisibleForReviewFilter(caseEntry, scope = 'club', filter = 'active') {
  if (filter === 'all') return true;
  const isOpen = isCaseOpenForScope(caseEntry, scope);
  return filter === 'closed' ? !isOpen : isOpen;
}

function formatReviewTrack(track = {}) {
  const status = String(track.status || 'open').replaceAll('_', ' ');
  const resolution = String(track.resolution || track.effectiveAction || '').replaceAll('_', ' ');
  return resolution ? `${status}: ${resolution}` : status;
}

function formatLastActionActor(caseEntry = {}) {
  if (caseEntry.lastActedByName) return caseEntry.lastActedByName;
  return caseEntry.lastActedRole === 'support' ? 'Member Nest support' : 'Club admin';
}

function getReasonLabel(reason) {
  if (typeof reason === 'string') return reason;
  return reason?.label || reason?.id || 'Review signal';
}

function getCaseActionValues(caseEntry = {}) {
  const clubTrack = getCaseReviewTrack(caseEntry, 'club');
  const platformTrack = getCaseReviewTrack(caseEntry, 'platform');
  return [
    caseEntry.lastClubAction,
    caseEntry.lastPlatformAction,
    caseEntry.resolution,
    clubTrack.action,
    clubTrack.effectiveAction,
    clubTrack.resolution,
    platformTrack.action,
    platformTrack.effectiveAction,
    platformTrack.resolution,
    platformTrack.override?.action,
  ].map((value) => String(value || '').trim()).filter(Boolean);
}

function getCaseSearchText(caseEntry = {}) {
  return [
    caseEntry.teamName,
    caseEntry.boardName,
    caseEntry.contentType,
    caseEntry.source,
    caseEntry.contentPreview,
    caseEntry.hiddenContentSnapshot?.bodyText,
    caseEntry.lastActedByName,
    getCaseReviewTrack(caseEntry, 'club').reviewedByName,
    getCaseReviewTrack(caseEntry, 'platform').reviewedByName,
    ...getCaseActionValues(caseEntry),
    ...(Array.isArray(caseEntry.reasons) ? caseEntry.reasons.map(getReasonLabel) : []),
  ].join(' ').toLowerCase();
}

function caseMatchesSearchAndFilters(caseEntry = {}, scope = 'club', statusFilter = 'active', search = '', filters = EMPTY_CASE_FILTERS) {
  if (!isCaseVisibleForReviewFilter(caseEntry, scope, statusFilter)) return false;
  const queryText = String(search || '').trim().toLowerCase();
  if (queryText && !getCaseSearchText(caseEntry).includes(queryText)) return false;
  if (filters.source && String(caseEntry.source || '') !== filters.source) return false;
  if (filters.contentType && String(caseEntry.contentType || '') !== filters.contentType) return false;
  if (filters.action && !getCaseActionValues(caseEntry).includes(filters.action)) return false;
  if (filters.teamId && caseEntry.teamId !== filters.teamId) return false;
  if (filters.boardId && caseEntry.boardId !== filters.boardId) return false;

  return true;
}

function caseFiltersAreActive(search = '', filters = EMPTY_CASE_FILTERS) {
  return String(search || '').trim().length > 0
    || Object.values(filters || {}).some((value) => String(value || '').trim().length > 0);
}

function PendingBadge({ label }) {
  return <span className="board-pending-badge">{label}</span>;
}

function StateBadge({ label, tone = 'neutral' }) {
  return <span className={`board-state-pill board-state-pill--${tone}`}>{label}</span>;
}

function EmptyState({
  title,
  description,
  actionLabel,
  onAction,
}) {
  return (
    <div className="board-empty-state">
      <strong>{title}</strong>
      <p className="text-muted">{description}</p>
      {actionLabel && onAction && (
        <button type="button" className="btn btn--primary btn--sm" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function BoardFormModal({
  isOpen,
  board,
  adminTeams = [],
  initialTeamId = '',
  teamGifSettings = {},
  onClose,
  onSave,
}) {
  const isEditing = Boolean(board?.id);
  const resolvedInitialTeamId = useMemo(() => (
    board?.teamId
      || initialTeamId
      || adminTeams[0]?.id
      || ''
  ), [adminTeams, board?.teamId, initialTeamId]);
  const [teamId, setTeamId] = useState(resolvedInitialTeamId);
  const [name, setName] = useState(board?.name || '');
  const [description, setDescription] = useState(board?.description || '');
  const [visibility, setVisibility] = useState(board?.visibility || 'open');
  const [allowedTagNames, setAllowedTagNames] = useState(board?.allowedTagNames || []);
  const [gifSettings, setGifSettings] = useState(() => normalizeBoardGifSettings(board?.gifSettings));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setTeamId(resolvedInitialTeamId);
    setName(board?.name || '');
    setDescription(board?.description || '');
    setVisibility(board?.visibility || 'open');
    setAllowedTagNames(board?.allowedTagNames || []);
    setGifSettings(normalizeBoardGifSettings(board?.gifSettings));
    setSaving(false);
    setError('');
  }, [board, isOpen, resolvedInitialTeamId]);

  if (!isOpen) return null;

  const selectedTeam = adminTeams.find((team) => team.id === teamId) || null;
  const tagOptions = selectedTeam?.tagOptions || [];

  const toggleTag = (tagName) => {
    setAllowedTagNames((current) => (
      current.includes(tagName)
        ? current.filter((value) => value !== tagName)
        : [...current, tagName]
    ));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await onSave({
        teamId,
        name: name.trim(),
        description: description.trim(),
        visibility,
        allowedTagNames: visibility === 'tags'
          ? Array.from(new Set(allowedTagNames.map((value) => String(value).trim()).filter(Boolean)))
          : [],
        gifSettings,
      });
    } catch (err) {
      setError(err?.message || 'Could not save board.');
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  const selectedTagCount = allowedTagNames.length;
  const selectedTeamGifSettings = normalizeTeamBoardGifSettings(
    selectedTeam?.communicationSettings?.gifs || teamGifSettings
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content modal-content--lg board-modal" onClick={(event) => event.stopPropagation()}>
        <div className="board-modal__header">
          <div>
            <h2 className="modal-title">{board?.id ? 'Edit Board' : 'Create Board'}</h2>
            <p className="text-muted board-modal__subtitle">
              Shape who can see this board and what kind of discussion it should hold.
            </p>
          </div>
        </div>

        {error && <p className="text-danger">{error}</p>}

        <form className="modal-form board-modal__form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Club</label>
            {isEditing ? (
              <input
                className="form-input"
                value={board?.teamName || selectedTeam?.name || 'Club'}
                disabled
                readOnly
              />
            ) : (
              <select
                className="form-input"
                value={teamId}
                onChange={(event) => {
                  setTeamId(event.target.value);
                  setAllowedTagNames([]);
                  setVisibility('open');
                }}
              >
                {adminTeams.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label>Name</label>
            <input
              className="form-input"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Coaching Updates"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-input"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Tell members what belongs in this board and how to use it."
            />
          </div>

          <div className="form-group">
            <label>Visibility</label>
            <div className="board-visibility-toggle" role="tablist" aria-label="Board visibility">
              <button
                type="button"
                className={`board-visibility-toggle__option ${visibility === 'open' ? 'active' : ''}`}
                onClick={() => setVisibility('open')}
              >
                <strong>Open</strong>
                <span>All team members with board access can see this board.</span>
              </button>
              <button
                type="button"
                className={`board-visibility-toggle__option ${visibility === 'tags' ? 'active' : ''}`}
                onClick={() => setVisibility('tags')}
              >
                <strong>Restricted</strong>
                <span>Only members with selected tags can open this board.</span>
              </button>
            </div>
          </div>

          {visibility === 'tags' && (
            <div className="board-settings-card">
              <div className="board-settings-card__header">
                <div>
                  <strong>Allowed tags</strong>
                  <p className="text-muted">
                    Members need at least one of these tags to see the board.
                  </p>
                </div>
                <span className="board-settings-card__count">
                  {selectedTagCount} selected
                </span>
              </div>

              {tagOptions.length === 0 ? (
                <div className="board-tag-empty">
                  <strong>No tags available</strong>
                  <p className="text-muted">
                    Add team tags first if you want to restrict this board to specific groups.
                  </p>
                </div>
              ) : (
                <div className="board-tag-chip-grid">
                  {tagOptions.map((tagName) => {
                    const active = allowedTagNames.includes(tagName);
                    return (
                      <button
                        key={tagName}
                        type="button"
                        className={`board-tag-chip ${active ? 'active' : ''}`}
                        onClick={() => toggleTag(tagName)}
                      >
                        <span>{tagName}</span>
                        <span className="board-tag-chip__state">{active ? 'Selected' : 'Select'}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="board-gif-settings-block">
            <h3>GIFs</h3>
            <p className="text-muted">
              Club default: {selectedTeamGifSettings.boardsEnabled ? 'enabled' : 'disabled'}, max rating {selectedTeamGifSettings.boardDefaultRating.toUpperCase()}.
            </p>
            <label className="form-group">
              <span className="form-label">Board setting</span>
              <select
                className="form-input"
                value={gifSettings.mode}
                onChange={(event) => setGifSettings((current) => ({
                  ...current,
                  mode: event.target.value,
                }))}
              >
                <option value="inherit">Use club default</option>
                <option value="disabled">Disable GIFs</option>
                <option value="custom">Custom rating</option>
              </select>
            </label>

            {gifSettings.mode === 'custom' && (
              <label className="form-group">
                <span className="form-label">Max rating</span>
                <select
                  className="form-input"
                  value={gifSettings.rating}
                  onChange={(event) => setGifSettings((current) => ({
                    ...current,
                    rating: event.target.value,
                  }))}
                >
                  {GIPHY_RATINGS.map((rating) => (
                    <option key={rating} value={rating}>{rating.toUpperCase()}</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={saving || !name.trim() || !teamId}>
              {saving ? 'Saving…' : board?.id ? 'Save Board' : 'Create Board'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function BoardDeleteDialog({ board, busy = false, onConfirm, onClose }) {
  return (
    <DestructiveActionDialog
      isOpen={Boolean(board)}
      title="Permanently delete this board?"
      message={`Deleting “${board?.name || 'this board'}” removes every topic, reply, moderation record, and attachment on it. This cannot be undone.`}
      confirmationText={board?.name || ''}
      confirmLabel="Delete board"
      cancelLabel="Keep board"
      busy={busy}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
}

function TopicComposerModal({
  isOpen,
  boardName,
  title,
  value,
  error,
  mediaItems = [],
  mediaBusy = false,
  saving = false,
  fileInputRef,
  searchMentionTargets,
  gifsEnabled = false,
  gifRating = 'g',
  onClose,
  onTitleChange,
  onChange,
  onAttachMedia,
  onRemoveMedia,
  onMentionMetadataChange,
  onSubmit,
}) {
  if (!isOpen) return null;

  const mediaAttachments = mediaItems.map((item) => item.media).filter(Boolean);
  const hasTitle = Boolean(String(title || '').trim());

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content modal-content--xl board-topic-modal" onClick={(event) => event.stopPropagation()}>
        <div className="board-topic-modal__hero">
          <div>
            <h2 className="modal-title">Start a Topic</h2>
            <p className="text-muted board-topic-modal__subtitle">
              Post a clear opener for {boardName || 'this board'} so members know what you want feedback on.
            </p>
          </div>
          <div className="board-topic-modal__pill">Discussion starter</div>
        </div>

        {error && <p className="text-danger">{error}</p>}

        <form
          className="modal-form"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(value);
          }}
        >
          <label className="form-label">
            Topic title
            <input
              type="text"
              className="form-input"
              value={title}
              onChange={(event) => onTitleChange(event.target.value)}
              maxLength={120}
              placeholder="Give members a clear thread title"
              disabled={saving}
            />
          </label>
          <RichTextEditor
            value={value}
            onChange={onChange}
            onMentionMetadataChange={onMentionMetadataChange}
            searchMentionTargets={searchMentionTargets}
            gifsEnabled={gifsEnabled}
            gifRating={gifRating}
            minHeight="min(280px, 42vh)"
            placeholder="Add the opening post, context, or question..."
          />
          {mediaItems.length > 0 && (
            <div className="board-media-composer">
              <ComposerMediaRail
                items={mediaItems}
                onRemove={onRemoveMedia}
                removeButtonClassName="board-media-composer__remove"
                busyStatuses={MEDIA_BUSY_STATUSES}
                statusDetail={mediaDraftStatusDetail}
              />
            </div>
          )}
          <div className="board-topic-modal__footer">
            <div className="board-topic-modal__hint text-muted">
              The title appears in the board list and notifications.
            </div>
            <div className="board-compose-actions board-topic-modal__actions">
              <div className="board-compose-actions__tools">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                  multiple
                  onChange={onAttachMedia}
                  hidden
                />
                <button
                  type="button"
                  className="board-compose-actions__tool"
                  onClick={() => fileInputRef?.current?.click()}
                  disabled={saving || mediaBusy}
                  aria-label="Attach photo or video"
                  title="Attach photo or video"
                >
                  <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05 12 20.49a6 6 0 0 1-8.49-8.49l9.44-9.44a4 4 0 0 1 5.66 5.66l-9.44 9.44a2 2 0 0 1-2.83-2.83l8.49-8.49" />
                  </svg>
                </button>
              </div>
              <div className="form-actions board-topic-modal__submit-actions">
                <button type="button" className="btn btn--ghost" onClick={onClose} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn--primary" disabled={saving || mediaBusy || !hasTitle}>
                  {saving ? 'Posting…' : mediaAttachments.length > 0 ? 'Post Topic with Media' : 'Post Topic'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditHistoryModal({
  isOpen,
  title,
  content,
  onClose,
}) {
  if (!isOpen || !content) return null;

  const history = Array.isArray(content.editHistory) ? content.editHistory : [];
  const versions = [
    {
      id: 'current',
      label: 'Current version',
      title: content.title || '',
      bodyHtml: content.bodyHtml || '',
      bodyText: content.bodyText || '',
      timestamp: content.updatedAt || content.createdAt,
      authorName: content.authorName || 'Member',
    },
    ...[...history].reverse().map((entry, index) => ({
      id: `history-${index}`,
      label: `Previous version ${history.length - index}`,
      title: entry.title || '',
      bodyHtml: entry.bodyHtml || '',
      bodyText: entry.bodyText || '',
      timestamp: entry.editedAt || entry.previousUpdatedAt,
      authorName: entry.authorName || content.authorName || 'Member',
    })),
  ];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content modal-content--lg board-history-modal" onClick={(event) => event.stopPropagation()}>
        <div className="board-modal__header">
          <div>
            <h2 className="modal-title">{title}</h2>
            <p className="text-muted board-modal__subtitle">
              Review the current text and prior saved versions.
            </p>
          </div>
        </div>

        <div className="board-history-list">
          {versions.map((version) => (
            <article key={version.id} className="board-history-entry">
              <div className="board-history-entry__meta">
                <strong>{version.label}</strong>
                <span>{version.authorName}</span>
                <span>{formatRelevantDateTime(version.timestamp)}</span>
              </div>
              {version.title && (
                <strong className="board-history-entry__title">{version.title}</strong>
              )}
              {version.bodyHtml ? (
                <BoardRichContent html={version.bodyHtml} />
              ) : (
                <p className="text-muted">{version.bodyText || 'No content captured.'}</p>
              )}
            </article>
          ))}
        </div>

        <div className="form-actions">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ModerationEvidenceModal({
  isOpen,
  evidence,
  loading = false,
  error = '',
  onClose,
}) {
  if (!isOpen) return null;

  const contentType = evidence?.contentType === 'reply' ? 'reply' : 'topic';
  const topic = evidence?.topic?.data || null;
  const reply = evidence?.reply?.data || null;
  const replies = Array.isArray(evidence?.replies) ? evidence.replies : [];
  const renderEvidenceContent = (entry, fallback = 'No retained content captured.') => (
    entry?.bodyHtml ? (
      <BoardRichContent html={entry.bodyHtml} />
    ) : (
      <p className="text-muted">{entry?.bodyText || fallback}</p>
    )
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content modal-content--lg board-history-modal" onClick={(event) => event.stopPropagation()}>
        <div className="board-modal__header">
          <div>
            <h2 className="modal-title">Retained Evidence</h2>
            <p className="text-muted board-modal__subtitle">
              Original content retained for Member Nest platform review.
            </p>
          </div>
        </div>

        {loading && <p className="text-muted">Loading retained evidence...</p>}
        {error && <p className="text-danger">{error}</p>}

        {!loading && !error && (
          <div className="board-history-list">
            {contentType === 'topic' && topic && (
              <article className="board-history-entry">
                <div className="board-history-entry__meta">
                  <strong>Original topic</strong>
                  <span>{topic.authorName || 'Member'}</span>
                  <span>{formatRelevantDateTime(topic.createdAt)}</span>
                </div>
                {renderEvidenceContent(topic)}
              </article>
            )}

            {contentType === 'reply' && reply && (
              <article className="board-history-entry">
                <div className="board-history-entry__meta">
                  <strong>Original reply</strong>
                  <span>{reply.authorName || 'Member'}</span>
                  <span>{formatRelevantDateTime(reply.createdAt)}</span>
                </div>
                {renderEvidenceContent(reply)}
              </article>
            )}

            {contentType === 'topic' && replies.length > 0 && (
              <article className="board-history-entry">
                <div className="board-history-entry__meta">
                  <strong>{replies.length} retained {replies.length === 1 ? 'reply' : 'replies'}</strong>
                </div>
                <div className="board-history-list">
                  {replies.map((entry) => (
                    <article key={entry.id} className="board-history-entry">
                      <div className="board-history-entry__meta">
                        <strong>{entry.data?.authorName || 'Member'}</strong>
                        <span>{formatRelevantDateTime(entry.data?.createdAt)}</span>
                      </div>
                      {renderEvidenceContent(entry.data)}
                    </article>
                  ))}
                </div>
              </article>
            )}

            {!topic && !reply && replies.length === 0 && (
              <p className="text-muted">No retained evidence is available for this case.</p>
            )}
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn btn--primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ModeSwitcher({
  isSupportAdmin,
  canViewModeration,
  activeMode,
  onChange,
  compact = false,
}) {
  const options = [
    { id: 'discussion', label: 'Discussion' },
    ...(canViewModeration ? [{ id: 'review', label: compact ? 'Review' : 'Review Queue' }] : []),
    ...(isSupportAdmin ? [{ id: 'support', label: compact ? 'Platform' : 'Platform Review' }] : []),
  ];

  if (options.length <= 1) return null;

  return (
    <div
      className="board-mode-switcher"
      role="tablist"
      aria-label="Board admin modes"
      style={{ '--board-mode-count': options.length }}
    >
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          className={`board-mode-switcher__option ${activeMode === option.id ? 'active' : ''}`}
          onClick={() => onChange(option.id)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function MobileBoardTrail({ steps = [] }) {
  if (!steps.length) return null;
  return (
    <nav className="boards-mobile-trail" aria-label="Board navigation">
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        return (
          <React.Fragment key={`${step.label}-${index}`}>
            {step.onClick && !isLast ? (
              <button type="button" className="boards-mobile-trail__step" onClick={step.onClick}>
                {step.label}
              </button>
            ) : (
              <span className={`boards-mobile-trail__step ${isLast ? 'active' : ''}`}>
                {step.label}
              </span>
            )}
            {!isLast && <span className="boards-mobile-trail__sep" aria-hidden="true">/</span>}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

function TopicListItem({
  topic,
  active,
  onSelect,
  showFlags,
  unreadCount = 0,
}) {
  const showGlobalPin = !topic.pending && Boolean(topic.isPinned);
  const showPersonalPin = !topic.pending && !showGlobalPin && Boolean(topic.isPersonalPinned);

  return (
    <button
      type="button"
      className={`board-topic-list-item ${active ? 'active' : ''}`}
      onClick={onSelect}
    >
      <div className="board-topic-list-item__topline">
        <strong>{getTopicTitle(topic)}</strong>
        <div className="board-topic-list-item__status">
          {unreadCount > 0 && (
            <span className="message-unread-badge" aria-label={`${unreadCount} unread update${unreadCount === 1 ? '' : 's'} in this topic`}>
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
          {topic.pending && <PendingBadge label="Posting" />}
          {showGlobalPin && <StateBadge label="Pinned" tone="global" />}
          {showPersonalPin && <StateBadge label="Pinned for me" tone="personal" />}
          {!topic.pending && topic.replyStatus === 'locked' && <StateBadge label="Locked" tone="neutral" />}
          {!topic.pending && showFlags ? <FlagBadge content={topic} /> : null}
        </div>
      </div>
      <div className="board-topic-list-item__meta">
        <span>{topic.authorName || 'Member'}</span>
        <span>{formatRelevantDateTime(topic.lastReplyAt || topic.createdAt)}</span>
        <span>{topic.replyCount || 0} replies</span>
      </div>
      <p className="board-topic-list-item__excerpt">{getTopicExcerpt(topic) || 'Open the thread to read the full discussion.'}</p>
    </button>
  );
}

function TopicListSkeleton() {
  return (
    <div className="board-topic-list board-topic-list--loading" aria-label="Loading topics">
      {[0, 1, 2, 3].map((index) => (
        <div key={index} className="board-topic-list-item board-topic-list-item--skeleton">
          <div className="board-topic-skeleton__line board-topic-skeleton__line--title" />
          <div className="board-topic-skeleton__meta">
            <span />
            <span />
            <span />
          </div>
          <div className="board-topic-skeleton__line board-topic-skeleton__line--excerpt" />
        </div>
      ))}
    </div>
  );
}

function TopicToolbar({
  canViewModeration,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  totalCount = 0,
  visibleCount = 0,
}) {
  const visibleFilters = TOPIC_FILTERS.filter((option) => !option.adminOnly || canViewModeration);

  return (
    <div className="board-topic-toolbar">
      <label className="board-topic-search">
        <span className="board-topic-search__label">Search topics</span>
        <input
          type="search"
          className="form-input board-topic-search__input"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search title, author, or content..."
        />
      </label>

      <div className="board-topic-toolbar__meta">
        <div className="board-topic-toolbar__filters" role="tablist" aria-label="Topic filters">
          {visibleFilters.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`board-topic-filter ${filter === option.id ? 'active' : ''}`}
              onClick={() => onFilterChange(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <span className="board-topic-toolbar__count text-muted">
          {visibleCount === totalCount ? `${totalCount} shown` : `${visibleCount} of ${totalCount} shown`}
        </span>
      </div>
    </div>
  );
}

function ReviewCaseToolbar({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  clubOptions = [],
  boardOptions = [],
  showClubFilter = false,
  resultCount = 0,
  totalCount = 0,
  usingArchive = false,
  loading = false,
}) {
  const updateFilter = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value,
      ...(key === 'teamId' ? { boardId: '' } : {}),
    });
  };
  const clearFilters = () => {
    onSearchChange('');
    onFiltersChange(EMPTY_CASE_FILTERS);
  };
  const active = caseFiltersAreActive(search, filters);

  return (
    <div className="board-review-toolbar">
      <label className="board-topic-search">
        <span className="board-topic-search__label">Search review cases</span>
        <input
          type="search"
          className="form-input board-topic-search__input"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search cases, clubs, boards, reasons, preview..."
        />
      </label>

      <div className="board-review-toolbar__filters">
        {showClubFilter && (
          <select
            className="form-input board-review-toolbar__select"
            value={filters.teamId}
            onChange={(event) => updateFilter('teamId', event.target.value)}
            aria-label="Filter cases by club"
          >
            <option value="">All clubs</option>
            {clubOptions.map((option) => (
              <option key={option.id} value={option.id}>{option.label}</option>
            ))}
          </select>
        )}
        <select
          className="form-input board-review-toolbar__select"
          value={filters.boardId}
          onChange={(event) => updateFilter('boardId', event.target.value)}
          aria-label="Filter cases by board"
        >
          <option value="">All boards</option>
          {boardOptions.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
        <select
          className="form-input board-review-toolbar__select"
          value={filters.contentType}
          onChange={(event) => updateFilter('contentType', event.target.value)}
          aria-label="Filter cases by content type"
        >
          <option value="">All content</option>
          {CASE_CONTENT_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
        <select
          className="form-input board-review-toolbar__select"
          value={filters.source}
          onChange={(event) => updateFilter('source', event.target.value)}
          aria-label="Filter cases by source"
        >
          <option value="">All sources</option>
          {CASE_SOURCE_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
        <select
          className="form-input board-review-toolbar__select"
          value={filters.action}
          onChange={(event) => updateFilter('action', event.target.value)}
          aria-label="Filter cases by action"
        >
          <option value="">All actions</option>
          {CASE_ACTION_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>{option.label}</option>
          ))}
        </select>
        {active && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={clearFilters}>
            Clear
          </button>
        )}
      </div>

      <div className="board-review-toolbar__meta">
        <span className="text-muted">
          {loading
            ? 'Loading review cases...'
            : `${resultCount}${totalCount > resultCount ? ` of ${totalCount}` : ''} shown${usingArchive ? ' from archive' : ' live'}`}
        </span>
      </div>
    </div>
  );
}

function ModerationCaseCard({
  caseEntry,
  busyAction,
  showTeamContext = false,
  reviewScope = 'club',
  highlighted = false,
  onOpenContext,
  onReview,
  onContactAdmin,
  onOpenEvidence,
}) {
  const clubTrack = getCaseReviewTrack(caseEntry, 'club');
  const platformTrack = getCaseReviewTrack(caseEntry, 'platform');
  const isPlatform = reviewScope === 'platform';
  const busyKey = (action, overrideAction = '') => `${reviewScope}-${action}-${overrideAction || ''}-${caseEntry.id}`;

  return (
    <article className={`board-case-card ${highlighted ? 'board-case-card--highlighted' : ''}`.trim()}>
      <div className="board-case-card__header">
        <div>
          <strong>
            {showTeamContext
              ? `${caseEntry.teamName || 'Team'} • ${caseEntry.boardName || 'Board'}`
              : caseEntry.boardName || 'Board'}
          </strong>
          <div className="text-muted board-case-card__meta">
            <span>{caseEntry.contentType}</span>
            <span>{formatDateTime(caseEntry.updatedAt || caseEntry.createdAt)}</span>
            {(caseEntry.reportCount || caseEntry.reportedBy?.length) > 0 && (
              <span>{caseEntry.reportCount || caseEntry.reportedBy.length} report{(caseEntry.reportCount || caseEntry.reportedBy.length) === 1 ? '' : 's'}</span>
            )}
          </div>
        </div>
        <span className="board-case-pill">{String(caseEntry.source || 'review').replaceAll('_', ' ')}</span>
      </div>

      <div className="board-case-track-row">
        <span className="board-state-pill board-state-pill--neutral">Club: {formatReviewTrack(clubTrack)}</span>
        <span className="board-state-pill board-state-pill--personal">Platform: {formatReviewTrack(platformTrack)}</span>
        {caseEntry.lastActedBy && (
          <span className="text-muted">
            Last action by {formatLastActionActor(caseEntry)}
          </span>
        )}
      </div>

      <div className="board-case-reasons">
        {(caseEntry.reasons || []).map((reason) => (
          <span key={getReasonLabel(reason)} className="board-tag-pill">{getReasonLabel(reason)}</span>
        ))}
      </div>

      <p className="board-case-preview">
        {caseEntry.hiddenContentSnapshot?.bodyText || caseEntry.contentPreview || 'No preview available.'}
      </p>

      <div className="board-case-card__action-groups">
        <div className="board-case-card__action-group">
          <span className="board-case-card__action-label">Review</span>
          <div className="board-case-card__action-row">
            <button type="button" className="btn btn--ghost btn--sm" onClick={onOpenContext}>
              Open Context
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={busyAction === busyKey(isPlatform ? 'accept_club_action' : 'left_visible')}
              onClick={() => onReview(caseEntry, isPlatform ? 'accept_club_action' : 'left_visible', { reviewScope })}
            >
              {isPlatform ? 'Accept Club Action' : 'Mark Reviewed'}
            </button>
            {isPlatform && (
              <>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  disabled={busyAction === `contact-admin-${caseEntry.id}`}
                  onClick={() => onContactAdmin?.(caseEntry)}
                >
                  Contact Admin
                </button>
                {caseEntry.supportEvidencePath && (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={busyAction === `evidence-${caseEntry.id}`}
                    onClick={() => onOpenEvidence?.(caseEntry)}
                  >
                    View Evidence
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="board-case-card__action-group">
          <span className="board-case-card__action-label">{isPlatform ? 'Override Content' : 'Content'}</span>
          <div className="board-case-card__action-row">
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={busyAction === busyKey(isPlatform ? 'override_club_action' : 'hide', isPlatform ? 'hide' : '')}
              onClick={() => onReview(caseEntry, isPlatform ? 'override_club_action' : 'hide', { reviewScope, overrideAction: isPlatform ? 'hide' : '' })}
            >
              Hide
            </button>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              disabled={busyAction === busyKey(isPlatform ? 'override_club_action' : 'delete_content', isPlatform ? 'delete_content' : '')}
              onClick={() => onReview(caseEntry, isPlatform ? 'override_club_action' : 'delete_content', { reviewScope, overrideAction: isPlatform ? 'delete_content' : '' })}
            >
              Delete
            </button>
            {isPlatform && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={busyAction === busyKey('override_club_action', 'left_visible')}
                onClick={() => onReview(caseEntry, 'override_club_action', { reviewScope, overrideAction: 'left_visible' })}
              >
                Restore Visible
              </button>
            )}
            {caseEntry.contentType === 'topic' && (
              <>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={busyAction === busyKey(isPlatform ? 'override_club_action' : 'lock_topic', isPlatform ? 'lock_topic' : '')}
                  onClick={() => onReview(caseEntry, isPlatform ? 'override_club_action' : 'lock_topic', { reviewScope, overrideAction: isPlatform ? 'lock_topic' : '' })}
                >
                  Lock Topic
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={busyAction === busyKey(isPlatform ? 'override_club_action' : 'unlock_topic', isPlatform ? 'unlock_topic' : '')}
                  onClick={() => onReview(caseEntry, isPlatform ? 'override_club_action' : 'unlock_topic', { reviewScope, overrideAction: isPlatform ? 'unlock_topic' : '' })}
                >
                  Unlock Topic
                </button>
              </>
            )}
          </div>
        </div>

        <div className="board-case-card__action-group">
          <span className="board-case-card__action-label">{isPlatform ? 'Override Restrictions' : 'Member Restrictions'}</span>
          <div className="board-case-card__action-row">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={busyAction === busyKey(isPlatform ? 'override_club_action' : 'mute_7d', isPlatform ? 'mute_7d' : '')}
              onClick={() => onReview(caseEntry, isPlatform ? 'override_club_action' : 'mute_7d', { reviewScope, overrideAction: isPlatform ? 'mute_7d' : '' })}
            >
              Mute 7d
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={busyAction === busyKey(isPlatform ? 'override_club_action' : 'mute_30d', isPlatform ? 'mute_30d' : '')}
              onClick={() => onReview(caseEntry, isPlatform ? 'override_club_action' : 'mute_30d', { reviewScope, overrideAction: isPlatform ? 'mute_30d' : '' })}
            >
              Mute 30d
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={busyAction === busyKey(isPlatform ? 'override_club_action' : 'unmute', isPlatform ? 'unmute' : '')}
              onClick={() => onReview(caseEntry, isPlatform ? 'override_club_action' : 'unmute', { reviewScope, overrideAction: isPlatform ? 'unmute' : '' })}
            >
              Unmute
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={busyAction === busyKey(isPlatform ? 'override_club_action' : 'revoke_board_access', isPlatform ? 'revoke_board_access' : '')}
              onClick={() => onReview(caseEntry, isPlatform ? 'override_club_action' : 'revoke_board_access', { reviewScope, overrideAction: isPlatform ? 'revoke_board_access' : '' })}
            >
              Remove Access
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={busyAction === busyKey(isPlatform ? 'override_club_action' : 'restore_board_access', isPlatform ? 'restore_board_access' : '')}
              onClick={() => onReview(caseEntry, isPlatform ? 'override_club_action' : 'restore_board_access', { reviewScope, overrideAction: isPlatform ? 'restore_board_access' : '' })}
            >
              Restore Access
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function BoardsView({
  user,
  searchParams,
  setSearchParams,
  unreadSummary = {},
  messageNotifications = [],
}) {
  const { isSupportAdmin } = useSupportAdminAccess(user?.uid || '');
  const { isStandalone } = usePwaInstallPrompt();
  const { isCompactPwaViewport } = useCompactPwaViewport(isStandalone);
  const onlineRefreshSignal = useOnlineRefreshSignal();
  const selectedTeamId = searchParams.get('teamId') || '';
  const selectedBoardId = searchParams.get('boardId') || '';
  const selectedTopicId = searchParams.get('topicId') || '';
  const selectedCaseId = searchParams.get('caseId') || '';
  const panel = searchParams.get('panel') || 'discussion';
  const selectedClubFilter = searchParams.get('clubId') || '';

  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [topicsState, setTopicsState] = useState({
    teamId: '',
    boardId: '',
    items: [],
    loading: false,
  });
  const [replies, setReplies] = useState([]);
  const [moderationCases, setModerationCases] = useState([]);
  const [supportCases, setSupportCases] = useState([]);
  const [reviewCaseFilter, setReviewCaseFilter] = useState('active');
  const [caseSearch, setCaseSearch] = useState('');
  const [caseFilters, setCaseFilters] = useState(EMPTY_CASE_FILTERS);
  const [archiveCases, setArchiveCases] = useState([]);
  const [archiveTotal, setArchiveTotal] = useState(0);
  const [caseSearchLoading, setCaseSearchLoading] = useState(false);
  const [boardModalState, setBoardModalState] = useState({ open: false, board: null });
  const [pendingBoardDelete, setPendingBoardDelete] = useState(null);
  const [topicModalOpen, setTopicModalOpen] = useState(false);
  const [historyModalState, setHistoryModalState] = useState({ open: false, title: '', content: null });
  const [evidenceModalState, setEvidenceModalState] = useState({ open: false, evidence: null, loading: false, error: '' });
  const [topicComposerTitle, setTopicComposerTitle] = useState('');
  const [topicComposerValue, setTopicComposerValue] = useState('');
  const [topicMentionMetadata, setTopicMentionMetadata] = useState(EMPTY_MENTION_METADATA);
  const [topicComposerError, setTopicComposerError] = useState('');
  const [topicMediaItems, setTopicMediaItems] = useState([]);
  const [replyDraft, setReplyDraft] = useState('');
  const [replyMediaItems, setReplyMediaItems] = useState([]);
  const [replyMentionMetadata, setReplyMentionMetadata] = useState(EMPTY_MENTION_METADATA);
  const [replyComposerExpanded, setReplyComposerExpanded] = useState(false);
  const [replyComposerPopoutOpen, setReplyComposerPopoutOpen] = useState(false);
  const [editingTopicId, setEditingTopicId] = useState('');
  const [editingTopicTitle, setEditingTopicTitle] = useState('');
  const [editingTopicDraft, setEditingTopicDraft] = useState('');
  const [editingTopicMentionMetadata, setEditingTopicMentionMetadata] = useState(EMPTY_MENTION_METADATA);
  const [editingReplyId, setEditingReplyId] = useState('');
  const [editingReplyDraft, setEditingReplyDraft] = useState('');
  const [editingReplyMentionMetadata, setEditingReplyMentionMetadata] = useState(EMPTY_MENTION_METADATA);
  const [pendingTopic, setPendingTopic] = useState(null);
  const [pendingReply, setPendingReply] = useState(null);
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');
  const [teamUiPreferences, setTeamUiPreferences] = useState({});
  const [topicSearch, setTopicSearch] = useState('');
  const [topicFilter, setTopicFilter] = useState('all');
  const [replySortOrder, setReplySortOrder] = useState('oldest');
  const [pinningTopicId, setPinningTopicId] = useState('');
  const [savingTopicId, setSavingTopicId] = useState('');
  const [isNarrowMobile, setIsNarrowMobile] = useState(() => window.innerWidth < 768);
  const [isCompactBoards, setIsCompactBoards] = useState(() => window.innerWidth <= 1280);
  const isMobile = isNarrowMobile || isCompactPwaViewport;
  const topicFileInputRef = useRef(null);
  const replyComposerRef = useRef(null);
  const replyFileInputRef = useRef(null);
  const latestUpdateRef = useRef(null);
  const [pendingLatestScroll, setPendingLatestScroll] = useState(false);
  const topics = useMemo(
    () => (
      topicsState.teamId === selectedTeamId && topicsState.boardId === selectedBoardId
        ? topicsState.items
        : []
    ),
    [selectedBoardId, selectedTeamId, topicsState.boardId, topicsState.items, topicsState.teamId]
  );
  const topicsLoading = Boolean(selectedTeamId && selectedBoardId) && (
    topicsState.loading
    || topicsState.teamId !== selectedTeamId
    || topicsState.boardId !== selectedBoardId
  );

  const setBoardState = useCallback((updates, replace = false) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', 'boards');
    Object.entries(updates).forEach(([key, value]) => {
      if (value == null || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    setSearchParams(next, { replace });
  }, [searchParams, setSearchParams]);

  const getListenerContext = useCallback((label, extra = {}) => ({
    label: `messages.boards.${label}`,
    uid: user?.uid || '',
    teamId: selectedTeamId,
    boardId: selectedBoardId,
    topicId: selectedTopicId,
    panel,
    ...extra,
  }), [panel, selectedBoardId, selectedTeamId, selectedTopicId, user?.uid]);

  useEffect(() => {
    const handleResize = () => {
      setIsNarrowMobile(window.innerWidth < 768);
      setIsCompactBoards(window.innerWidth <= 1280);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setTeams([]);
      setTeamsLoading(false);
      return;
    }

    const cachedTeams = readOfflineContentCache([user.uid, CACHE_SCOPE, 'teams'], []);
    setTeams(cachedTeams);
    setTeamsLoading(cachedTeams.length === 0);

    let cancelled = false;
    const loadTeams = async () => {
      if (cachedTeams.length === 0) setTeamsLoading(true);
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const teamRoles = userSnap.exists() ? (userSnap.data()?.team_roles || {}) : {};
        const discoveredIds = new Set(Object.keys(teamRoles || {}));
        if (selectedTeamId) discoveredIds.add(selectedTeamId);
        const nextTeams = [];
        await Promise.all(Array.from(discoveredIds).map(async (teamId) => {
          const [teamSnap, memberSnap, assignmentSnap] = await Promise.all([
            getDoc(doc(db, 'teams', teamId)),
            getDoc(doc(db, 'teams', teamId, 'members', user.uid)).catch(() => null),
            getDoc(doc(db, 'teams', teamId, 'adminAssignments', user.uid)).catch(() => null),
          ]);

          if (!teamSnap.exists()) return;
          const teamData = teamSnap.data() || {};
          const boardsEnabledForTeam = teamData?.modules?.communications?.boards?.enabled === true;
          const eventCommentsEnabledForTeam = teamData?.eventSettings?.commentsEnabled === true;
          let hasBoardSurface = boardsEnabledForTeam || eventCommentsEnabledForTeam;
          if (!hasBoardSurface) {
            const boardsSnap = await getDocs(query(collection(db, 'teams', teamId, 'boards'), limit(1))).catch(() => ({ empty: true }));
            hasBoardSurface = !boardsSnap.empty;
          }
          if (!hasBoardSurface) return;

          const admins = Array.isArray(teamData.admins) ? teamData.admins : [];
          const assignmentKeys = assignmentSnap?.exists() && Array.isArray(assignmentSnap.data()?.permissionKeys)
            ? assignmentSnap.data().permissionKeys
            : [];
          const isFullAdmin = admins.includes(user.uid);
          const canManageCommunications = assignmentKeys.includes('communications.manage');
          const isAdmin = isFullAdmin || canManageCommunications;
          const isMember = Boolean(memberSnap?.exists());
          if (!isAdmin && !isMember && !isSupportAdmin) return;

          const memberData = memberSnap?.exists() ? (memberSnap.data() || {}) : {};
          const hasBoardAccessRevoked = Boolean(memberData?.boardPrivilegesRevokedAt);
          if (!isAdmin && !isSupportAdmin && hasBoardAccessRevoked) return;

          nextTeams.push({
            id: teamId,
            name: teamData.name || 'Untitled team',
            isAdmin,
            isMember,
            boardsEnabled: boardsEnabledForTeam,
            eventCommentsEnabled: eventCommentsEnabledForTeam,
            communicationSettings: teamData.communicationSettings || {},
            tagNames: normalizeMemberTags(memberData.tags),
            tagOptions: extractTeamTagOptions(teamData.tags),
          });
        }));

        if (cancelled) return;

        const sortedTeams = [...nextTeams].sort((left, right) => left.name.localeCompare(right.name));
        setTeams(sortedTeams);
        writeOfflineContentCache([user.uid, CACHE_SCOPE, 'teams'], sortedTeams);
      } catch (err) {
        if (!cancelled) {
          if (cachedTeams.length === 0) setTeams([]);
          setError('Could not load board teams.');
        }
      } finally {
        if (!cancelled) {
          setTeamsLoading(false);
        }
      }
    };

    loadTeams();
    return () => {
      cancelled = true;
    };
  }, [isSupportAdmin, onlineRefreshSignal, selectedTeamId, user?.uid]);

  const selectedTeam = useMemo(
    () => teams.find((team) => team.id === selectedTeamId) || null,
    [teams, selectedTeamId]
  );

  const selectedClub = useMemo(
    () => teams.find((team) => team.id === selectedClubFilter) || null,
    [selectedClubFilter, teams]
  );

  useEffect(() => {
    if (!user?.uid || teams.length === 0) return undefined;
    const unsubscribers = teams
      .filter((team) => team.isMember || team.isAdmin)
      .map((team) => safeOnSnapshot(
        doc(db, 'teams', team.id, 'members', user.uid),
        (snap) => {
          const nextTagNames = snap.exists()
            ? normalizeMemberTags(snap.data()?.tags).sort((a, b) => a.localeCompare(b))
            : [];

          setTeams((currentTeams) => {
            let changed = false;
            const nextTeams = currentTeams.map((currentTeam) => {
              if (currentTeam.id !== team.id) return currentTeam;
              const currentTagNames = Array.isArray(currentTeam.tagNames)
                ? currentTeam.tagNames.slice().sort((a, b) => a.localeCompare(b))
                : [];
              if (areSameStringList(currentTagNames, nextTagNames)) return currentTeam;
              changed = true;
              return {
                ...currentTeam,
                tagNames: nextTagNames,
              };
            });            return changed ? nextTeams : currentTeams;
          });
        },
        (err) => {        },
        getListenerContext('memberTags', { teamId: team.id })
      ));

    return () => {      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [getListenerContext, teams, user?.uid]);

  const handleClubFilterChange = useCallback((teamId) => {
    if (!teamId) {
      setBoardState({ clubId: null }, true);
      return;
    }
    setBoardState({ clubId: teamId, teamId, boardId: null, topicId: null, panel: 'discussion' }, true);
  }, [setBoardState]);

  useEffect(() => {
    if (!user?.uid || !selectedTeamId) {
      setTeamUiPreferences({});
      return undefined;
    }

    const settingsRef = doc(db, 'users', user.uid, 'teamSettings', selectedTeamId);
    const unsubscribe = safeOnSnapshot(
      settingsRef,
      (snap) => {
        setTeamUiPreferences(snap.exists() ? (snap.data()?.uiPreferences || {}) : {});
      },
      () => {
        setTeamUiPreferences({});
      },
      getListenerContext('teamUiPreferences')
    );

    return () => unsubscribe();
  }, [getListenerContext, selectedTeamId, user?.uid]);

  useEffect(() => {
    if (teams.length === 0) {
      setBoards([]);
      setBoardsLoading(false);
      return undefined;
    }

    const cachedBoards = readOfflineContentCache([user?.uid || 'unknown', CACHE_SCOPE, 'list'], []);
    setBoards(cachedBoards);
    setBoardsLoading(true);
    const boardsByTeam = new Map();
    cachedBoards.forEach((board) => {
      if (!board?.teamId) return;
      const current = boardsByTeam.get(board.teamId) || [];
      current.push(board);
      boardsByTeam.set(board.teamId, current);
    });
    const pendingTeamIds = new Set(teams.map((team) => team.id));
    const unsubscribeGroups = [];
    const publish = () => {
      const merged = sortByActivity(Array.from(boardsByTeam.values()).flat());
      setBoards(merged);
      writeOfflineContentCache([user?.uid || 'unknown', CACHE_SCOPE, 'list'], merged);
      setBoardsLoading(pendingTeamIds.size > 0);
    };
    const markTeamLoaded = (teamId) => {
      pendingTeamIds.delete(teamId);
      publish();
    };

    teams.forEach((team) => {
      const collectionRef = collection(db, 'teams', team.id, 'boards');
      const decorateBoard = (docSnap) => ({
        id: docSnap.id,
        teamId: team.id,
        teamName: team.name,
        teamIsAdmin: team.isAdmin,
        teamTagOptions: team.tagOptions || [],
        boardsEnabled: team.boardsEnabled === true,
        eventCommentsEnabled: team.eventCommentsEnabled === true,
        teamGifSettings: team.communicationSettings?.gifs || {},
        ...docSnap.data(),
      });

      if (team.isAdmin) {
        const unsubscribe = safeOnSnapshot(collectionRef, (snap) => {
          boardsByTeam.set(team.id, snap.docs.map(decorateBoard));
          markTeamLoaded(team.id);
        }, (err) => {
          if (!boardsByTeam.has(team.id)) boardsByTeam.set(team.id, []);
          markTeamLoaded(team.id);
        }, {
          label: 'messages.boards.boards',
          uid: user?.uid || '',
          teamId: team.id,
        });
        unsubscribeGroups.push(unsubscribe);
        return;
      }

      let cancelled = false;
      const loadAccessibleBoards = async () => {
        try {
          const result = await listAccessibleBoards({ teamId: team.id });
          if (cancelled) return;
          const accessibleBoards = (Array.isArray(result?.boards) ? result.boards : []).map((board) => ({
            ...board,
            teamId: team.id,
            teamName: team.name,
            teamIsAdmin: team.isAdmin,
            teamTagOptions: team.tagOptions || [],
            boardsEnabled: team.boardsEnabled === true,
            eventCommentsEnabled: team.eventCommentsEnabled === true,
            teamGifSettings: team.communicationSettings?.gifs || {},
          }));
          boardsByTeam.set(team.id, accessibleBoards);
          markTeamLoaded(team.id);
        } catch (err) {
          if (cancelled) return;
          if (!boardsByTeam.has(team.id)) boardsByTeam.set(team.id, []);
          setError(err?.message || 'Could not load boards.');
          markTeamLoaded(team.id);
        }
      };

      loadAccessibleBoards();
      unsubscribeGroups.push(() => {
        cancelled = true;
      });
    });

    return () => {
      unsubscribeGroups.forEach((unsubscribe) => unsubscribe());
    };
  }, [isSupportAdmin, onlineRefreshSignal, teams, user?.uid]);

  useEffect(() => {
    if (!user?.uid || isSupportAdmin || teamsLoading || teams.length > 0 || messageNotifications.length === 0) return;

    const staleBoardNotifications = messageNotifications.filter((notification) => {
      const normalized = normalizeMessageNotification(notification);
      return normalized?.type === 'boards';
    });
    if (staleBoardNotifications.length === 0) return;

    Promise.all(staleBoardNotifications.map((notification) => (
      deleteDoc(doc(db, 'notifications', user.uid, 'items', notification.id))
    ))).catch(() => {});
  }, [isSupportAdmin, messageNotifications, teams.length, teamsLoading, user?.uid]);

  const discussionBoards = useMemo(
    () => boards.filter(isDiscussionBoard),
    [boards]
  );

  const visibleBoards = useMemo(
    () => discussionBoards.filter((board) => !selectedClubFilter || board.teamId === selectedClubFilter),
    [discussionBoards, selectedClubFilter]
  );

  const selectedBoard = useMemo(
    () => discussionBoards.find((board) => board.id === selectedBoardId && board.teamId === selectedTeamId) || null,
    [discussionBoards, selectedBoardId, selectedTeamId]
  );
  const selectedBoardIsEventComments = Boolean(selectedBoard && isEventCommentsBoard(selectedBoard));
  const selectedBoardNormalLocked = Boolean(selectedBoard && !selectedBoardIsEventComments && selectedBoard.boardsEnabled === false);
  const selectedBoardEventCommentsLocked = Boolean(selectedBoardIsEventComments && selectedBoard?.eventCommentsEnabled !== true);
  const selectedBoardReadOnly = selectedBoardNormalLocked || selectedBoardEventCommentsLocked;
  const canWriteSelectedBoard = Boolean(selectedBoard && !selectedBoardReadOnly);
  const selectedBoardGifPolicy = useMemo(() => resolveBoardGifPolicy({
    teamGifSettings: selectedBoard?.teamGifSettings,
    boardGifSettings: selectedBoard?.gifSettings,
  }), [selectedBoard?.gifSettings, selectedBoard?.teamGifSettings]);

  const pinnedTopicIdsByBoard = useMemo(
    () => normalizePinnedTopicIdsByBoard(teamUiPreferences?.boards?.pinnedTopicIdsByBoard),
    [teamUiPreferences]
  );

  const personalPinnedTopicIds = useMemo(
    () => pinnedTopicIdsByBoard[selectedBoardId] || [],
    [pinnedTopicIdsByBoard, selectedBoardId]
  );

  const personalPinnedTopicIdSet = useMemo(
    () => new Set(personalPinnedTopicIds),
    [personalPinnedTopicIds]
  );

  const canManageTeamBoards = Boolean(selectedTeam?.isAdmin);
  const canViewModeration = Boolean(selectedTeam?.isAdmin || isSupportAdmin);
  const shouldLoadSupportCases = panel === 'support' && isSupportAdmin;
  const adminTeams = useMemo(
    () => teams.filter((team) => team.isAdmin && team.boardsEnabled === true),
    [teams]
  );
  const canCreateTeamBoards = adminTeams.length > 0;

  const handleSearchMentionTargets = useCallback(async ({ query: mentionQuery = '' } = {}) => {
    if (!selectedTeamId || !selectedBoardId) return [];
    const result = await searchBoardMentionTargets({
      teamId: selectedTeamId,
      boardId: selectedBoardId,
      eventId: selectedBoardIsEventComments ? selectedTopicId : undefined,
      query: mentionQuery,
    });
    return Array.isArray(result?.targets) ? result.targets : [];
  }, [selectedBoardId, selectedBoardIsEventComments, selectedTeamId, selectedTopicId]);

  useEffect(() => {
    if (boardsLoading) return;
    if (visibleBoards.length === 0) {
      if (selectedBoardId) setBoardState({ boardId: null, topicId: null }, true);
      return;
    }

    const selectedBoardIsVisible = visibleBoards.some((board) => (
      board.id === selectedBoardId && board.teamId === selectedTeamId
    ));

    if (selectedBoardId && !selectedBoardIsVisible) {
      setBoardState({ boardId: null, topicId: null }, true);
      return;
    }

    if (!isMobile && !selectedBoardId) {
      const unreadBoard = visibleBoards.find((board) => (
        Number(unreadSummary?.byBoard?.[boardNotificationKey(board.teamId, board.id)] || 0) > 0
      ));
      const nextBoard = unreadBoard || visibleBoards[0];
      setBoardState({ teamId: nextBoard.teamId, boardId: nextBoard.id, topicId: null }, true);
    }
  }, [boardsLoading, isMobile, selectedBoardId, selectedTeamId, setBoardState, unreadSummary?.byBoard, visibleBoards]);

  useEffect(() => {
    setPendingTopic(null);
    setPendingReply(null);
    setTopicComposerError('');
    setTopicSearch('');
    setTopicFilter('all');
    setEditingTopicId('');
    setEditingTopicTitle('');
    setEditingTopicDraft('');
    setEditingTopicMentionMetadata(EMPTY_MENTION_METADATA);
    setEditingReplyId('');
    setEditingReplyDraft('');
    setEditingReplyMentionMetadata(EMPTY_MENTION_METADATA);
    setReplyDraft('');
    setReplyMentionMetadata(EMPTY_MENTION_METADATA);
  }, [selectedBoardId]);

  useEffect(() => {
    if (!selectedTeamId || !selectedBoardId || !selectedBoard) {
      setTopicsState({
        teamId: selectedTeamId || '',
        boardId: selectedBoardId || '',
        items: [],
        loading: false,
      });
      return;
    }

    const topicCacheParts = [user?.uid || 'unknown', CACHE_SCOPE, 'topics', selectedTeamId, selectedBoardId];
    const cachedTopics = readOfflineContentCache(topicCacheParts, []);
    if (boardsLoading) {
      setTopicsState({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        items: cachedTopics,
        loading: true,
      });
      return;
    }

    setTopicsState({
      teamId: selectedTeamId,
      boardId: selectedBoardId,
      items: cachedTopics,
      loading: cachedTopics.length === 0,
    });

    if (selectedBoardIsEventComments) {
      let cancelled = false;
      listVisibleEventCommentTopics({ teamId: selectedTeamId })
        .then((result) => {
          if (cancelled) return;
          setTopicsState({
            teamId: selectedTeamId,
            boardId: selectedBoardId,
            items: Array.isArray(result?.topics) ? result.topics : [],
            loading: false,
          });
          writeOfflineContentCache(topicCacheParts, Array.isArray(result?.topics) ? result.topics : []);
          setError('');
        })
        .catch((err) => {
          if (cancelled) return;
          setTopicsState({
            teamId: selectedTeamId,
            boardId: selectedBoardId,
            items: cachedTopics,
            loading: false,
          });
          setError(err?.message || 'Could not load event comment topics.');
        });
      return () => {
        cancelled = true;
      };
    }

    const topicsRef = collection(db, 'teams', selectedTeamId, 'boards', selectedBoardId, 'topics');
    return safeOnSnapshot(topicsRef, (snap) => {
      const nextTopics = [...snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))].sort((left, right) => {
        const diff = timeValue(right?.lastReplyAt || right?.createdAt) - timeValue(left?.lastReplyAt || left?.createdAt);
        if (diff !== 0) return diff;
        return timeValue(right?.createdAt) - timeValue(left?.createdAt);
      });
      setTopicsState({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        items: nextTopics,
        loading: false,
      });
      writeOfflineContentCache(topicCacheParts, nextTopics.slice(0, 100));
    }, (err) => {
      if (isPermissionDeniedError(err)) {
        setTopicsState({
          teamId: selectedTeamId,
          boardId: selectedBoardId,
          items: [],
          loading: false,
        });
        writeOfflineContentCache(topicCacheParts, []);
        setError('This board is no longer available to your account.');
        setBoardState({ boardId: null, topicId: null }, true);
        return;
      }
      setTopicsState({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        items: cachedTopics,
        loading: false,
      });
      setError(err?.message || 'Could not load board topics.');
    }, getListenerContext('topics', { suppressPermissionDenied: true }));
  }, [boardsLoading, getListenerContext, onlineRefreshSignal, selectedBoard, selectedBoardId, selectedBoardIsEventComments, selectedTeamId, setBoardState, user?.uid]);

  const topicItems = useMemo(() => {
    const visibleSourceTopics = topics.filter((topic) => (
      topic?.moderationStatus !== 'deleted'
      && (canViewModeration || topic?.moderationStatus !== 'hidden')
    ));
    const source = pendingTopic && pendingTopic.boardId === selectedBoardId
      ? [pendingTopic, ...visibleSourceTopics]
      : visibleSourceTopics;

    const withPins = source.map((topic) => ({
      ...topic,
      isPersonalPinned: personalPinnedTopicIdSet.has(topic.id),
    }));

    const personalPinOrder = new Map(personalPinnedTopicIds.map((topicId, index) => [topicId, index]));

    return [...withPins].sort((left, right) => {
      if (left.pending !== right.pending) return left.pending ? -1 : 1;

      const leftGlobal = Boolean(left.isPinned);
      const rightGlobal = Boolean(right.isPinned);
      if (leftGlobal !== rightGlobal) return leftGlobal ? -1 : 1;
      if (leftGlobal && rightGlobal) {
        const pinnedDiff = timeValue(right.pinnedAt) - timeValue(left.pinnedAt);
        if (pinnedDiff !== 0) return pinnedDiff;
      }

      const leftPersonal = Boolean(left.isPersonalPinned);
      const rightPersonal = Boolean(right.isPersonalPinned);
      if (leftPersonal !== rightPersonal) return leftPersonal ? -1 : 1;
      if (leftPersonal && rightPersonal) {
        const personalDiff = (personalPinOrder.get(left.id) ?? 0) - (personalPinOrder.get(right.id) ?? 0);
        if (personalDiff !== 0) return personalDiff;
      }

      const activityDiff = timeValue(right?.lastReplyAt || right?.createdAt) - timeValue(left?.lastReplyAt || left?.createdAt);
      if (activityDiff !== 0) return activityDiff;
      return timeValue(right?.createdAt) - timeValue(left?.createdAt);
    });
  }, [canViewModeration, pendingTopic, personalPinnedTopicIdSet, personalPinnedTopicIds, selectedBoardId, topics]);

  const visibleTopics = useMemo(() => {
    const searchTerm = topicSearch.trim().toLowerCase();

    return topicItems.filter((topic) => {
      if (searchTerm && !getTopicSearchText(topic).includes(searchTerm)) {
        return false;
      }

      if (topicFilter === 'pinned') {
        return Boolean(topic.isPinned || topic.isPersonalPinned);
      }
      if (topicFilter === 'mine') {
        return topic.authorId === user?.uid;
      }
      if (topicFilter === 'unanswered') {
        return Number(topic.replyCount || 0) === 0;
      }
      if (topicFilter === 'flagged') {
        return canViewModeration && topic.flagStatus && topic.flagStatus !== 'clear';
      }
      return true;
    });
  }, [canViewModeration, topicFilter, topicItems, topicSearch, user?.uid]);

  useEffect(() => {
    if (topicsLoading) return;
    if (!selectedBoardId) {
      if (selectedTopicId) setBoardState({ topicId: null }, true);
      return;
    }

    if (visibleTopics.length === 0) {
      if (selectedTopicId) setBoardState({ topicId: null }, true);
      return;
    }

    const selectedTopicIsVisible = visibleTopics.some((topic) => topic.id === selectedTopicId);
    if (selectedTopicId && !selectedTopicIsVisible) {
      setBoardState({ topicId: null }, true);
      return;
    }

    if (!isMobile && !selectedTopicId) {
      const unreadTopic = visibleTopics.find((topic) => (
        Number(unreadSummary?.byTopic?.[boardNotificationKey(selectedTeamId, selectedBoardId, topic.id)] || 0) > 0
      ));
      setBoardState({ topicId: (unreadTopic || visibleTopics[0]).id, panel: panel === 'support' ? 'support' : panel }, true);
    }
  }, [isMobile, panel, selectedBoardId, selectedTeamId, selectedTopicId, setBoardState, topicsLoading, unreadSummary?.byTopic, visibleTopics]);

  const selectedTopic = useMemo(
    () => visibleTopics.find((topic) => topic.id === selectedTopicId) || null,
    [selectedTopicId, visibleTopics]
  );
  const selectedTopicUnreadCount = Number(
    unreadSummary?.byTopic?.[boardNotificationKey(selectedTeamId, selectedBoardId, selectedTopicId)] || 0
  );

  useEffect(() => {
    if (!user?.uid || messageNotifications.length === 0) return;
    const matchingNotifications = messageNotifications.filter((notification) => {
      const normalized = normalizeMessageNotification(notification);
      return normalized?.moderationCaseId && isSelectedBoardNotification(notification, {
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        caseId: selectedCaseId,
      });
    });
    if (matchingNotifications.length === 0) return;

    Promise.all(matchingNotifications.map((notification) => (
      deleteDoc(doc(db, 'notifications', user.uid, 'items', notification.id))
    ))).catch(() => {});
  }, [messageNotifications, selectedBoardId, selectedCaseId, selectedTeamId, user?.uid]);

  useEffect(() => {
    if (!selectedTeamId || !selectedBoardId || !selectedTopicId || isPendingId(selectedTopicId)) {
      setReplies([]);
      return;
    }

    const replyCacheParts = [user?.uid || 'unknown', CACHE_SCOPE, 'replies', selectedTeamId, selectedBoardId, selectedTopicId];
    const cachedReplies = readOfflineContentCache(replyCacheParts, []);
    setReplies(cachedReplies);
    const repliesRef = collection(db, 'teams', selectedTeamId, 'boards', selectedBoardId, 'topics', selectedTopicId, 'replies');
    return safeOnSnapshot(repliesRef, (snap) => {
      const nextReplies = [...snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }))].sort((left, right) => timeValue(left?.createdAt) - timeValue(right?.createdAt));
      setReplies(nextReplies);
      writeOfflineContentCache(replyCacheParts, nextReplies.slice(-200));
    }, (err) => {
      setReplies(cachedReplies);
      setError(err?.message || 'Could not load topic replies.');
    }, getListenerContext('replies'));
  }, [getListenerContext, onlineRefreshSignal, selectedBoardId, selectedTeamId, selectedTopicId, user?.uid]);

  useEffect(() => {
    setPendingReply(null);
    setReplyDraft('');
    setReplyMentionMetadata(EMPTY_MENTION_METADATA);
    setEditingReplyId('');
    setEditingReplyDraft('');
    setEditingReplyMentionMetadata(EMPTY_MENTION_METADATA);
  }, [selectedTopicId]);

  useEffect(() => {
    if (!(selectedTeam?.isAdmin || isSupportAdmin) || !selectedTeamId) {
      setModerationCases([]);
      return;
    }
    if (boards.length === 0) {
      setModerationCases([]);
      return;
    }

    const casesBySource = new Map();
    const publishCases = () => {
      const merged = Array.from(casesBySource.values()).flat();
      setModerationCases(sortModerationCases(mergeDocMaps(merged)));
    };

    const teamCasesQuery = collection(db, 'teams', selectedTeamId, 'boardModerationCases');
    const unsubscribers = [
      safeOnSnapshot(teamCasesQuery, (snap) => {
        casesBySource.set('team', snap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            key: `${data.teamId || selectedTeamId}-${data.boardId || ''}-${docSnap.id}`,
            id: docSnap.id,
            ...data,
          };
        }));
        publishCases();
      }, () => {
        casesBySource.set('team', []);
        publishCases();
      }, getListenerContext('moderationCases.team')),
    ];

    boards.filter((board) => board.teamId === selectedTeamId).forEach((board) => {
      const casesQuery = collection(db, 'teams', selectedTeamId, 'boards', board.id, 'moderationCases');

      unsubscribers.push(safeOnSnapshot(casesQuery, (snap) => {
        casesBySource.set(`board-${board.id}`, snap.docs.map((docSnap) => {
          const data = docSnap.data() || {};
          return {
            key: `${data.teamId || selectedTeamId}-${data.boardId || board.id}-${docSnap.id}`,
            id: docSnap.id,
            ...data,
          };
        }));
        publishCases();
      }, () => {
        casesBySource.set(`board-${board.id}`, []);
        publishCases();
      }, getListenerContext('moderationCases.board', { boardId: board.id })));
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [boards, getListenerContext, isSupportAdmin, selectedTeam?.isAdmin, selectedTeamId]);

  useEffect(() => {
    if (!shouldLoadSupportCases) {
      setSupportCases([]);
      return;
    }
    let cancelled = false;
    const loadSupportCases = async () => {
      try {
        const result = await searchBoardModerationCases({
          scope: 'platform',
          statusFilter: 'active',
          teamId: selectedTeamId || '',
          limit: 100,
        });
        if (cancelled) return;
        setSupportCases(sortModerationCases(Array.isArray(result?.cases) ? result.cases : []));
      } catch (err) {
        if (cancelled) return;
        setSupportCases([]);
        setError(err?.message || 'Could not load platform review cases.');
      }
    };

    loadSupportCases();
    return () => {
      cancelled = true;
    };
  }, [onlineRefreshSignal, selectedTeamId, shouldLoadSupportCases]);

  const activeMode = shouldLoadSupportCases
    ? 'support'
    : (panel === 'moderation' || panel === 'review') && canViewModeration
      ? 'review'
      : 'discussion';
  const showSupportQueue = activeMode === 'support';
  const showModerationPanel = activeMode === 'review';
  const currentReviewScope = showSupportQueue ? 'platform' : 'club';
  const usingCaseArchive = showSupportQueue && reviewCaseFilter !== 'active';
  const liveCaseSource = showSupportQueue ? supportCases : moderationCases;
  const currentCaseSource = usingCaseArchive ? archiveCases : liveCaseSource;
  const currentCases = useMemo(() => {
    const filtered = currentCaseSource.filter((caseEntry) => caseMatchesSearchAndFilters(
      caseEntry,
      currentReviewScope,
      reviewCaseFilter,
      caseSearch,
      caseFilters
    ));

    if (!selectedCaseId) return filtered;
    return [...filtered].sort((left, right) => {
      if (left.id === selectedCaseId) return -1;
      if (right.id === selectedCaseId) return 1;
      return 0;
    });
  }, [caseFilters, caseSearch, currentCaseSource, currentReviewScope, reviewCaseFilter, selectedCaseId]);
  const reviewCaseCounts = useMemo(() => ({
    active: liveCaseSource.filter((caseEntry) => isCaseOpenForScope(caseEntry, currentReviewScope)).length,
    closed: usingCaseArchive ? archiveTotal : liveCaseSource.filter((caseEntry) => !isCaseOpenForScope(caseEntry, currentReviewScope)).length,
    all: usingCaseArchive ? archiveTotal : liveCaseSource.length,
  }), [archiveTotal, currentReviewScope, liveCaseSource, usingCaseArchive]);
  const caseToolsActive = caseFiltersAreActive(caseSearch, caseFilters);
  const caseClubOptions = useMemo(() => {
    const byId = new Map();
    [...liveCaseSource, ...archiveCases].forEach((caseEntry) => {
      if (!caseEntry?.teamId) return;
      byId.set(caseEntry.teamId, caseEntry.teamName || 'Club');
    });
    teams.forEach((team) => {
      if (team?.id) byId.set(team.id, team.name || 'Club');
    });
    if (caseFilters.teamId && !byId.has(caseFilters.teamId)) {
      byId.set(caseFilters.teamId, caseFilters.teamId);
    }
    return Array.from(byId.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [archiveCases, caseFilters.teamId, liveCaseSource, teams]);
  const caseBoardOptions = useMemo(() => (
    showSupportQueue
      ? Array.from(
        new Map(
          [...liveCaseSource, ...archiveCases]
            .filter((caseEntry) => !caseFilters.teamId || caseEntry.teamId === caseFilters.teamId)
            .filter((caseEntry) => caseEntry?.boardId)
            .map((caseEntry) => [caseEntry.boardId, caseEntry.boardName || 'Board'])
        ).entries()
      )
        .map(([id, label]) => ({ id, label }))
        .sort((left, right) => left.label.localeCompare(right.label))
      : boards
        .filter((board) => board.teamId === selectedTeamId)
        .map((board) => ({ id: board.id, label: board.name || 'Untitled board' }))
        .sort((left, right) => left.label.localeCompare(right.label))
  ), [archiveCases, boards, caseFilters.teamId, liveCaseSource, selectedTeamId, showSupportQueue]);

  useEffect(() => {
    setCaseFilters((current) => (
      showSupportQueue
        ? { ...current, boardId: '' }
        : { ...current, teamId: '' }
    ));
  }, [showSupportQueue]);

  useEffect(() => {
    if (!usingCaseArchive) {
      setArchiveCases([]);
      setArchiveTotal(0);
      setCaseSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setCaseSearchLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = await searchBoardModerationCases({
          scope: currentReviewScope,
          statusFilter: reviewCaseFilter,
          query: caseSearch,
          teamId: caseFilters.teamId,
          boardId: caseFilters.boardId,
          source: caseFilters.source,
          contentType: caseFilters.contentType,
          action: caseFilters.action,
          limit: 100,
        });
        if (cancelled) return;
        setArchiveCases(sortModerationCases(Array.isArray(result?.cases) ? result.cases : []));
        setArchiveTotal(Number(result?.total) || 0);
      } catch (err) {
        if (cancelled) return;
        setArchiveCases([]);
        setArchiveTotal(0);
        setError(err?.message || 'Could not search review cases.');
      } finally {
        if (!cancelled) setCaseSearchLoading(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [caseFilters, caseSearch, currentReviewScope, reviewCaseFilter, usingCaseArchive]);

  const topicToolsAreActive = topicFilter !== 'all' || topicSearch.trim().length > 0;

  const threadReplies = useMemo(() => {
    const baseReplies = selectedTopic?.pending
      ? []
      : replies.filter((reply) => (
        reply?.moderationStatus !== 'deleted'
        && (canViewModeration || reply?.moderationStatus !== 'hidden')
      ));
    const sortedReplies = [...baseReplies].sort((left, right) => {
      const diff = timeValue(left?.createdAt) - timeValue(right?.createdAt);
      if (diff !== 0) return replySortOrder === 'newest' ? -diff : diff;
      return String(left?.id || '').localeCompare(String(right?.id || ''));
    });
    if (pendingReply && pendingReply.topicId === selectedTopicId) {
      return replySortOrder === 'newest'
        ? [pendingReply, ...sortedReplies]
        : [...sortedReplies, pendingReply];
    }
    return sortedReplies;
  }, [canViewModeration, pendingReply, replies, replySortOrder, selectedTopic?.pending, selectedTopicId]);
  const latestReplyId = useMemo(() => {
    const publishedReplies = threadReplies.filter((reply) => !reply.pending);
    if (publishedReplies.length === 0) return '';
    return publishedReplies.reduce((latest, reply) => (
      timeValue(reply?.createdAt) > timeValue(latest?.createdAt) ? reply : latest
    ), publishedReplies[0])?.id || '';
  }, [threadReplies]);
  const selectedTopicHasReplyUnread = useMemo(() => messageNotifications.some((notification) => {
    const normalized = normalizeMessageNotification(notification);
    return notification?.kind === 'board_reply'
      && normalized?.teamId === selectedTeamId
      && normalized?.boardId === selectedBoardId
      && normalized?.topicId === selectedTopicId;
  }), [messageNotifications, selectedBoardId, selectedTeamId, selectedTopicId]);
  const topicIsLatestUnreadTarget = Boolean(
    selectedTopicUnreadCount > 0
    && !latestReplyId
    && !selectedTopicHasReplyUnread
  );

  const clearSelectedTopicNotifications = useCallback(() => {
    if (!user?.uid) return;
    const matchingNotifications = messageNotifications.filter((notification) => {
      const normalized = normalizeMessageNotification(notification);
      return !normalized?.moderationCaseId && isSelectedBoardNotification(notification, {
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        topicId: selectedTopicId,
      });
    });
    if (matchingNotifications.length === 0) return;

    Promise.all(matchingNotifications.map((notification) => (
      deleteDoc(doc(db, 'notifications', user.uid, 'items', notification.id))
    ))).catch(() => {});
  }, [messageNotifications, selectedBoardId, selectedTeamId, selectedTopicId, user?.uid]);

  const clearModerationCaseNotifications = useCallback((caseEntry = {}) => {
    if (!user?.uid || !caseEntry?.id) return;
    const matchingNotifications = messageNotifications.filter((notification) => {
      const normalized = normalizeMessageNotification(notification);
      if (!normalized?.moderationCaseId || normalized.moderationCaseId !== caseEntry.id) return false;
      if (caseEntry.teamId && normalized.teamId && normalized.teamId !== caseEntry.teamId) return false;
      if (caseEntry.boardId && normalized.boardId && normalized.boardId !== caseEntry.boardId) return false;
      return true;
    });
    if (matchingNotifications.length === 0) return;

    Promise.all(matchingNotifications.map((notification) => (
      deleteDoc(doc(db, 'notifications', user.uid, 'items', notification.id))
    ))).catch(() => {});
  }, [messageNotifications, user?.uid]);

  const clearIfLatestUpdateVisible = useCallback(() => {
    if (!user?.uid || !selectedTopicUnreadCount || (!latestReplyId && !topicIsLatestUnreadTarget)) return false;
    if (!isElementOnScreen(latestUpdateRef.current)) return false;
    clearSelectedTopicNotifications();
    return true;
  }, [clearSelectedTopicNotifications, latestReplyId, selectedTopicUnreadCount, topicIsLatestUnreadTarget, user?.uid]);

  useEffect(() => {
    if (!user?.uid || !selectedTopicUnreadCount || (!latestReplyId && !topicIsLatestUnreadTarget) || !latestUpdateRef.current) return undefined;

    const observer = new IntersectionObserver((entries) => {
      const latestIsVisible = entries.some((entry) => entry.isIntersecting);
      if (!latestIsVisible) return;
      clearIfLatestUpdateVisible();
    }, {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0,
    });

    observer.observe(latestUpdateRef.current);
    return () => observer.disconnect();
  }, [clearIfLatestUpdateVisible, latestReplyId, replySortOrder, selectedTopicUnreadCount, topicIsLatestUnreadTarget, user?.uid]);

  useEffect(() => {
    if (!user?.uid || !selectedTopicUnreadCount || (!latestReplyId && !topicIsLatestUnreadTarget)) return undefined;
    let frameId = window.requestAnimationFrame(() => {
      frameId = 0;
      clearIfLatestUpdateVisible();
    });
    const timeoutId = window.setTimeout(clearIfLatestUpdateVisible, 180);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [clearIfLatestUpdateVisible, latestReplyId, replySortOrder, selectedTopicUnreadCount, threadReplies.length, topicIsLatestUnreadTarget, user?.uid]);

  useEffect(() => {
    if (!user?.uid || !selectedTopicUnreadCount || (!latestReplyId && !topicIsLatestUnreadTarget)) return undefined;
    let frameId = 0;
    const scheduleVisibilityCheck = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        clearIfLatestUpdateVisible();
      });
    };

    window.addEventListener('scroll', scheduleVisibilityCheck, true);
    window.addEventListener('resize', scheduleVisibilityCheck);

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', scheduleVisibilityCheck, true);
      window.removeEventListener('resize', scheduleVisibilityCheck);
    };
  }, [clearIfLatestUpdateVisible, latestReplyId, selectedTopicUnreadCount, topicIsLatestUnreadTarget, user?.uid]);

  useEffect(() => {
    setReplyMediaItems([]);
  }, [selectedTopicId]);

  useEffect(() => {
    if (!pendingLatestScroll || (!latestReplyId && !topicIsLatestUnreadTarget)) return;
    if (!latestUpdateRef.current) return;
    latestUpdateRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPendingLatestScroll(false);
    window.setTimeout(clearSelectedTopicNotifications, 450);
  }, [clearSelectedTopicNotifications, latestReplyId, pendingLatestScroll, replySortOrder, topicIsLatestUnreadTarget]);

  const handleJumpToLatestUnread = useCallback(() => {
    if (!latestUpdateRef.current) {
      setPendingLatestScroll(true);
      return;
    }
    latestUpdateRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(clearSelectedTopicNotifications, 450);
  }, [clearSelectedTopicNotifications]);

  const visibleFlaggedItems = useMemo(() => {
    if (showSupportQueue) return [];

    const items = [];
    topicItems.forEach((topic) => {
      if (topic?.pending) return;
      if (topic?.flagStatus && topic.flagStatus !== 'clear') {
        items.push({
          key: `topic-${topic.id}`,
          contentType: 'topic',
          contentId: topic.id,
          topicId: topic.id,
          title: getTopicTitle(topic),
          preview: topic.bodyText || '',
          flagStatus: topic.flagStatus,
          createdAt: topic.updatedAt || topic.createdAt,
        });
      }
    });

    threadReplies.forEach((reply) => {
      if (reply?.pending) return;
      if (reply?.flagStatus && reply.flagStatus !== 'clear') {
        items.push({
          key: `reply-${reply.id}`,
          contentType: 'reply',
          contentId: reply.id,
          topicId: selectedTopicId,
          title: `Reply from ${reply.authorName || 'Member'}`,
          preview: reply.bodyText || '',
          flagStatus: reply.flagStatus,
          createdAt: reply.updatedAt || reply.createdAt,
        });
      }
    });

    const caseKeys = new Set(moderationCases.map((entry) => `${entry.contentType}-${entry.contentId}`));
    return items
      .filter((entry) => !caseKeys.has(`${entry.contentType}-${entry.contentId}`))
      .sort((left, right) => timeValue(right.createdAt) - timeValue(left.createdAt));
  }, [moderationCases, selectedTopicId, showSupportQueue, threadReplies, topicItems]);

  const submitWithWarnings = async (html, callback) => {
    const safeHtml = sanitizeBoardHtml(html);
    const bodyText = boardHtmlToText(safeHtml);
    if (!bodyText) return;
    await callback({ bodyHtml: safeHtml, warningShown: false });
  };

  const persistPersonalPinnedTopics = useCallback(async (nextTopicIds) => {
    if (!user?.uid || !selectedTeamId || !selectedBoardId) return;

    const settingsRef = doc(db, 'users', user.uid, 'teamSettings', selectedTeamId);
    const nextPinnedTopicIdsByBoard = {
      ...normalizePinnedTopicIdsByBoard(teamUiPreferences?.boards?.pinnedTopicIdsByBoard),
      [selectedBoardId]: normalizeIds(nextTopicIds),
    };

    const nextUiPreferences = {
      ...teamUiPreferences,
      boards: {
        ...(teamUiPreferences?.boards || {}),
        pinnedTopicIdsByBoard: nextPinnedTopicIdsByBoard,
      },
    };

    setTeamUiPreferences(nextUiPreferences);
    await setDoc(settingsRef, {
      uiPreferences: nextUiPreferences,
    }, { merge: true });
  }, [selectedBoardId, selectedTeamId, teamUiPreferences, user?.uid]);

  const togglePersonalPin = useCallback(async (topicId) => {
    const normalizedTopicId = typeof topicId === 'string' ? topicId.trim() : '';
    if (!normalizedTopicId || isPendingId(normalizedTopicId) || !selectedBoardId) return;

    const isPinned = personalPinnedTopicIdSet.has(normalizedTopicId);
    const nextTopicIds = isPinned
      ? personalPinnedTopicIds.filter((id) => id !== normalizedTopicId)
      : [...personalPinnedTopicIds, normalizedTopicId];

    setSavingTopicId(normalizedTopicId);
    setError('');
    try {
      await persistPersonalPinnedTopics(nextTopicIds);
    } catch (err) {
      setError(err?.message || 'Could not update saved topics.');
    } finally {
      setSavingTopicId('');
    }
  }, [personalPinnedTopicIdSet, personalPinnedTopicIds, persistPersonalPinnedTopics, selectedBoardId]);

  const toggleGlobalPin = useCallback(async (topic) => {
    const topicId = typeof topic?.id === 'string' ? topic.id.trim() : '';
    if (!topicId || isPendingId(topicId) || !selectedTeamId || !selectedBoardId || !canWriteSelectedBoard || selectedBoardIsEventComments) return;

    setPinningTopicId(topicId);
    setError('');
    try {
      await setBoardTopicPinned({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        topicId,
        pinned: !topic?.isPinned,
      });
    } catch (err) {
      setError(err?.message || 'Could not update pinned topic.');
    } finally {
      setPinningTopicId('');
    }
  }, [canWriteSelectedBoard, selectedBoardId, selectedBoardIsEventComments, selectedTeamId]);

  const handleCreateTopic = async (draftHtml = topicComposerValue) => {
    const topicMediaBusy = topicMediaItems.some((item) => MEDIA_BUSY_STATUSES.has(item.status));
    const topicMediaAttachments = topicMediaItems.map((item) => item.media).filter(Boolean);
    if (!selectedTeamId || !selectedBoardId || busyAction || topicMediaBusy || !canWriteSelectedBoard || selectedBoardIsEventComments) return;

    const title = String(topicComposerTitle || '').replace(/\s+/g, ' ').trim();
    if (!title) {
      setTopicComposerError('Topic title is required.');
      return;
    }
    const safeHtml = sanitizeBoardHtml(draftHtml);

    const optimisticTopic = buildPendingTopic({
      title,
      bodyHtml: safeHtml,
      user,
      boardId: selectedBoardId,
      mediaAttachments: topicMediaAttachments,
    });

    setBusyAction('create-topic');
    setError('');
    setTopicComposerError('');
    setPendingTopic(optimisticTopic);
    const mediaItemsToSend = topicMediaItems;
    const mediaAttachmentsToSend = topicMediaAttachments;
    setTopicMediaItems([]);
    setTopicModalOpen(false);
    setBoardState({ topicId: optimisticTopic.id, panel: 'discussion' }, true);

    try {
      const result = await createBoardTopic({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        title,
        bodyHtml: safeHtml,
        ...(mediaAttachmentsToSend.length > 0 ? { mediaUploadIds: mediaAttachmentsToSend.map((media) => media.uploadId) } : {}),
        mentionMetadata: normalizeMentionMetadata(topicMentionMetadata),
        warningShown: false,
      });
      setPendingTopic(null);
      setTopicComposerTitle('');
      setTopicComposerValue('');
      setTopicMediaItems([]);
      setTopicMentionMetadata(EMPTY_MENTION_METADATA);
      setBoardState({ topicId: result?.topicId || null, panel: 'discussion' }, true);
    } catch (err) {
      setPendingTopic(null);
      setError(err?.message || 'Could not create topic.');
      setTopicComposerError(err?.message || 'Could not create topic.');
      setTopicMediaItems(isMediaUploadAlreadyUsedError(err) ? [] : mediaItemsToSend);
      setTopicModalOpen(true);
      setBoardState({ topicId: null }, true);
    } finally {
      setBusyAction('');
    }
  };

  const discardTopicDraftMedia = useCallback((media) => {
    if (media?.provider !== 'firebase_upload' || !media?.uploadId) return;
    discardMediaUpload(media.uploadId).catch(() => {});
  }, []);

  const handleAttachTopicMedia = async (event) => {
    const files = Array.from(event.target.files || []).filter(Boolean);
    event.target.value = '';
    const topicMediaBusy = topicMediaItems.some((item) => MEDIA_BUSY_STATUSES.has(item.status));
    if (files.length === 0 || !selectedTeamId || !selectedBoardId || busyAction || !canWriteSelectedBoard || selectedBoardIsEventComments || topicMediaBusy) return;

    setTopicComposerError('');
    setError('');
    try {
      validateMediaAttachmentFiles(files, topicMediaItems.map((item) => item.media).filter(Boolean));
    } catch (err) {
      setTopicComposerError(err?.message || 'Could not attach this media.');
      return;
    }

    const draftItems = files.map((file, index) => ({
      id: mediaDraftItemId(file, index),
      fileName: file.name,
      status: 'preparing',
      progress: 0,
      media: null,
      error: '',
    }));
    setTopicMediaItems((current) => [...current, ...draftItems]);

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const itemId = draftItems[index].id;
      try {
        const media = await uploadModeratedMedia({
          file,
          surface: 'board_topic',
          target: {
            teamId: selectedTeamId,
            boardId: selectedBoardId,
          },
          onProgress: (progress) => setTopicMediaItems((current) => current.map((item) => (
            item.id === itemId ? { ...item, progress } : item
          ))),
          onStateChange: ({ status }) => setTopicMediaItems((current) => current.map((item) => (
            item.id === itemId ? { ...item, status } : item
          ))),
        });
        setTopicMediaItems((current) => current.map((item) => (
          item.id === itemId ? { ...item, media, status: 'approved', progress: 100, error: '' } : item
        )));
      } catch (err) {
        setTopicMediaItems((current) => current.map((item) => (
          item.id === itemId ? { ...item, status: 'error', error: err?.message || 'Could not attach this media.' } : item
        )));
        setTopicComposerError(err?.message || 'Could not attach this media.');
      }
    }
  };

  const handleUpdateTopic = async () => {
    if (!selectedTeamId || !selectedBoardId || !editingTopicId || busyAction || !canWriteSelectedBoard) return;
    const title = String(editingTopicTitle || '').replace(/\s+/g, ' ').trim();
    if (!selectedBoardIsEventComments && !title) {
      setError('Topic title is required.');
      return;
    }
    setBusyAction('update-topic');
    setError('');
    try {
      const safeHtml = sanitizeBoardHtml(editingTopicDraft);
      await updateBoardTopic({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        topicId: editingTopicId,
        ...(!selectedBoardIsEventComments ? { title } : {}),
        bodyHtml: safeHtml,
        mentionMetadata: normalizeMentionMetadata(editingTopicMentionMetadata),
        warningShown: false,
      });
      setEditingTopicId('');
      setEditingTopicTitle('');
      setEditingTopicDraft('');
      setEditingTopicMentionMetadata(EMPTY_MENTION_METADATA);
    } catch (err) {
      setError(err?.message || 'Could not update topic.');
    } finally {
      setBusyAction('');
    }
  };

  const discardReplyDraftMedia = useCallback((media) => {
    if (media?.provider !== 'firebase_upload' || !media?.uploadId) return;
    discardMediaUpload(media.uploadId).catch(() => {});
  }, []);

  const discardReplyDraftMediaItems = useCallback((items = []) => {
    items.forEach((item) => discardReplyDraftMedia(item?.media || item));
  }, [discardReplyDraftMedia]);

  const handleAttachReplyMedia = async (event) => {
    const files = Array.from(event.target.files || []).filter(Boolean);
    event.target.value = '';
    const replyMediaBusy = replyMediaItems.some((item) => MEDIA_BUSY_STATUSES.has(item.status));
    if (files.length === 0 || !selectedTeamId || !selectedBoardId || !selectedTopicId || busyAction || !canWriteSelectedBoard || replyMediaBusy) return;

    setError('');
    try {
      validateMediaAttachmentFiles(files, replyMediaItems.map((item) => item.media).filter(Boolean));
    } catch (err) {
      setError(err?.message || 'Could not attach this media.');
      return;
    }

    const draftItems = files.map((file, index) => ({
      id: mediaDraftItemId(file, index),
      fileName: file.name,
      status: 'preparing',
      progress: 0,
      media: null,
      error: '',
    }));
    setReplyMediaItems((current) => [...current, ...draftItems]);
    setReplyComposerExpanded(true);

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const itemId = draftItems[index].id;
      try {
        const media = await uploadModeratedMedia({
          file,
          surface: 'board_reply',
          target: {
            teamId: selectedTeamId,
            boardId: selectedBoardId,
            topicId: selectedTopicId,
          },
          onProgress: (progress) => setReplyMediaItems((current) => current.map((item) => (
            item.id === itemId ? { ...item, progress } : item
          ))),
          onStateChange: ({ status }) => setReplyMediaItems((current) => current.map((item) => (
            item.id === itemId ? { ...item, status } : item
          ))),
        });
        setReplyMediaItems((current) => current.map((item) => (
          item.id === itemId ? { ...item, media, status: 'approved', progress: 100, error: '' } : item
        )));
      } catch (err) {
        setReplyMediaItems((current) => current.map((item) => (
          item.id === itemId ? { ...item, status: 'error', error: err?.message || 'Could not attach this media.' } : item
        )));
        setError(err?.message || 'Could not attach this media.');
      }
    }
  };

  const handleCreateReply = async (topicId = selectedTopicId) => {
    const replyMediaBusy = replyMediaItems.some((item) => MEDIA_BUSY_STATUSES.has(item.status));
    const replyMediaAttachments = replyMediaItems.map((item) => item.media).filter(Boolean);
    if (!selectedTeamId || !selectedBoardId || !topicId || busyAction || replyMediaBusy || !canWriteSelectedBoard) return;
    if (selectedTopic?.id === topicId && selectedTopic.replyStatus === 'locked') {
      setError('This topic is locked. Replies are disabled until a club admin unlocks it.');
      return;
    }

    const safeHtml = sanitizeBoardHtml(replyDraft);
    const bodyText = boardHtmlToText(safeHtml);
    if (!bodyText && replyMediaAttachments.length === 0) {
      setError('Reply content is required.');
      return;
    }

    const optimisticReply = buildPendingReply({
      bodyHtml: safeHtml,
      user,
      topicId,
      mediaAttachments: replyMediaAttachments,
    });

    setBusyAction('create-reply');
    setError('');
    setPendingReply(optimisticReply);
    const mediaItemsToSend = replyMediaItems;
    const mediaAttachmentsToSend = replyMediaAttachments;
    setReplyDraft('');
    setReplyMediaItems([]);

    try {
      await createBoardReply({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        topicId,
        bodyHtml: safeHtml,
        ...(mediaAttachmentsToSend.length > 0 ? { mediaUploadIds: mediaAttachmentsToSend.map((media) => media.uploadId) } : {}),
        mentionMetadata: normalizeMentionMetadata(replyMentionMetadata),
        warningShown: false,
      });
      setPendingReply(null);
      setReplyMentionMetadata(EMPTY_MENTION_METADATA);
      setReplyComposerExpanded(false);
      setReplyComposerPopoutOpen(false);
    } catch (err) {
      setPendingReply(null);
      setReplyDraft(safeHtml);
      setReplyMediaItems(isMediaUploadAlreadyUsedError(err) ? [] : mediaItemsToSend);
      setReplyComposerExpanded(true);
      setError(err?.message || 'Could not add reply.');
    } finally {
      setBusyAction('');
    }
  };

  const handleUpdateReply = async (topicId = selectedTopicId) => {
    if (!selectedTeamId || !selectedBoardId || !topicId || !editingReplyId || busyAction || !canWriteSelectedBoard) return;
    setBusyAction('update-reply');
    setError('');
    try {
      await submitWithWarnings(editingReplyDraft, async ({ bodyHtml, warningShown }) => {
        await updateBoardReply({
          teamId: selectedTeamId,
          boardId: selectedBoardId,
          topicId,
          replyId: editingReplyId,
          bodyHtml,
          mentionMetadata: normalizeMentionMetadata(editingReplyMentionMetadata),
          warningShown,
        });
        setEditingReplyId('');
        setEditingReplyDraft('');
        setEditingReplyMentionMetadata(EMPTY_MENTION_METADATA);
      });
    } catch (err) {
      setError(err?.message || 'Could not update reply.');
    } finally {
      setBusyAction('');
    }
  };

  const handleReportContent = async ({ contentType, contentId, topicId }) => {
    if (!canWriteSelectedBoard) return;
    const reason = window.prompt('Why are you flagging this for review?');
    if (reason === null) return;
    setBusyAction(`report-${contentId}`);
    setError('');
    try {
      await reportBoardContent({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        topicId,
        contentType,
        contentId,
        reason: reason.trim(),
      });
    } catch (err) {
      setError(err?.message || 'Could not report content.');
    } finally {
      setBusyAction('');
    }
  };

  const handleDeleteMyContent = async ({ contentType, contentId, topicId }) => {
    if (!canWriteSelectedBoard || !selectedTeamId || !selectedBoardId || !contentType || !contentId || !topicId) return;
    const label = contentType === 'topic' ? 'topic' : 'reply';
    const confirmMessage = contentType === 'topic'
      ? 'Remove this topic and all replies from club discussion? The original content will be retained for moderation evidence.'
      : `Remove this ${label} from club discussion? The original content will be retained for moderation evidence.`;
    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    const busyKey = `self-delete-${contentType}-${contentId}`;
    setBusyAction(busyKey);
    setError('');
    try {
      await deleteMyBoardContent({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        topicId,
        contentType,
        contentId,
      });
      if (contentType === 'topic') {
        setBoardState({ topicId: null }, true);
        setEditingTopicId('');
        setEditingTopicTitle('');
        setEditingTopicDraft('');
        setEditingTopicMentionMetadata(EMPTY_MENTION_METADATA);
      } else {
        setReplies((current) => current.filter((reply) => reply.id !== contentId));
        if (editingReplyId === contentId) {
          setEditingReplyId('');
          setEditingReplyDraft('');
          setEditingReplyMentionMetadata(EMPTY_MENTION_METADATA);
        }
      }
    } catch (err) {
      setError(err?.message || `Could not delete this ${label}.`);
    } finally {
      setBusyAction('');
    }
  };

  const handleReviewFlaggedItem = async ({ contentType, contentId, topicId }, action = 'left_visible') => {
    const busyKey = `flagged-${action}-${contentId}`;
    setBusyAction(busyKey);
    setError('');
    try {
      const result = await ensureBoardModerationCase({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        topicId,
        contentType,
        contentId,
      });
      await reviewBoardModerationCase({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        caseId: result.caseId,
        action,
        reviewScope: 'club',
      });
    } catch (err) {
      setError(err?.message || 'Could not review flagged content.');
    } finally {
      setBusyAction('');
    }
  };

  const handleTopicModerationAction = async (topic, action) => {
    if (!topic?.id || !selectedTeamId || !selectedBoardId || !canWriteSelectedBoard) return;
    if (action === 'delete_content' && !window.confirm('Remove this topic from club discussion? Member Nest support will retain the original content for review.')) return;
    const busyKey = `topic-${action}-${topic.id}`;
    setBusyAction(busyKey);
    setError('');
    try {
      const result = await ensureBoardModerationCase({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        topicId: topic.id,
        contentType: 'topic',
        contentId: topic.id,
      });
      await reviewBoardModerationCase({
        teamId: selectedTeamId,
        boardId: selectedBoardId,
        caseId: result.caseId,
        action,
        reviewScope: 'club',
      });
      if (action === 'delete_content') {
        setBoardState({ topicId: null }, true);
      }
    } catch (err) {
      setError(err?.message || 'Could not moderate topic.');
    } finally {
      setBusyAction('');
    }
  };

  const handleSaveBoard = async (payload) => {
    const targetTeamId = boardModalState.board?.teamId || payload.teamId || selectedTeamId;
    if (!targetTeamId) return;
    const targetTeam = teams.find((team) => team.id === targetTeamId);
    if (targetTeam?.boardsEnabled !== true) {
      setError('Message Boards are read-only for this club.');
      setBoardModalState({ open: false, board: null });
      return;
    }
    const nowPayload = {
      name: payload.name,
      description: payload.description,
      visibility: payload.visibility,
      allowedTagNames: payload.allowedTagNames || [],
      gifSettings: normalizeBoardGifSettings(payload.gifSettings),
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
    };

    if (boardModalState.board?.id) {
      await updateDoc(doc(db, 'teams', targetTeamId, 'boards', boardModalState.board.id), nowPayload);
      setBoardModalState({ open: false, board: null });
      return;
    }

    const boardRef = doc(collection(db, 'teams', targetTeamId, 'boards'));
    await setDoc(boardRef, {
      ...nowPayload,
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      lastActivityAt: serverTimestamp(),
      topicCount: 0,
      replyCount: 0,
    });
    setBoardModalState({ open: false, board: null });
    setBoardState({ teamId: targetTeamId, clubId: targetTeamId, boardId: boardRef.id, topicId: null }, true);
  };

  const handleDeleteBoard = async () => {
    const board = pendingBoardDelete;
    if (!board?.id || !board?.teamId || !canWriteSelectedBoard || selectedBoardIsEventComments) return;
    setBusyAction(`delete-board-${board.id}`);
    setError('');
    try {
      await deleteBoard({ teamId: board.teamId, boardId: board.id });
      setPendingBoardDelete(null);
      setBoardState({ boardId: null, topicId: null }, true);
    } catch (err) {
      setPendingBoardDelete(null);
      setError(err?.message || 'Could not delete board.');
    } finally {
      setBusyAction('');
    }
  };

  const handleReviewCase = async (caseEntry, action, options = {}) => {
    if (!caseEntry?.id) return;
    const overrideAction = options.overrideAction || '';
    const effectiveAction = action === 'override_club_action' ? overrideAction : action;
    const reviewScope = options.reviewScope || (showSupportQueue ? 'platform' : 'club');
    if (effectiveAction === 'delete_content' && !window.confirm('Remove this content from club discussion? Member Nest support will retain the original content for review.')) return;
    if (effectiveAction === 'revoke_board_access' && !window.confirm('Remove this member from all message board access for this club?')) return;
    setBusyAction(`${reviewScope}-${action}-${overrideAction || ''}-${caseEntry.id}`);
    setError('');
    try {
      await reviewBoardModerationCase({
        teamId: caseEntry.teamId,
        boardId: caseEntry.boardId,
        caseId: caseEntry.id,
        action,
        reviewScope,
        overrideAction,
      });
      clearModerationCaseNotifications(caseEntry);
    } catch (err) {
      setError(err?.message || 'Could not review moderation case.');
    } finally {
      setBusyAction('');
    }
  };

  const handleContactAdmin = async (caseEntry) => {
    if (!caseEntry?.id) return;
    setBusyAction(`contact-admin-${caseEntry.id}`);
    setError('');
    try {
      const result = await openBoardModerationAdminThread({
        teamId: caseEntry.teamId,
        boardId: caseEntry.boardId,
        caseId: caseEntry.id,
      });
      if (result?.threadId) {
        window.location.assign(`/messages/${encodeURIComponent(result.threadId)}`);
      }
    } catch (err) {
      setError(err?.message || 'Could not open a conversation with the club admin.');
    } finally {
      setBusyAction('');
    }
  };

  const handleOpenEvidence = async (caseEntry) => {
    if (!caseEntry?.id) return;
    setBusyAction(`evidence-${caseEntry.id}`);
    setEvidenceModalState({ open: true, evidence: null, loading: true, error: '' });
    try {
      const result = await getBoardModerationEvidence({
        teamId: caseEntry.teamId,
        boardId: caseEntry.boardId,
        caseId: caseEntry.id,
      });
      setEvidenceModalState({
        open: true,
        evidence: result?.evidence || null,
        loading: false,
        error: '',
      });
    } catch (err) {
      setEvidenceModalState({
        open: true,
        evidence: null,
        loading: false,
        error: err?.message || 'Could not load retained evidence.',
      });
    } finally {
      setBusyAction('');
    }
  };

  const renderRichContent = (content) => (
    <>
      {content?.bodyHtml && (
        <BoardRichContent html={content?.bodyHtml || ''} />
      )}
      <MediaAttachmentGrid
        media={content?.media}
        mediaAttachments={content?.mediaAttachments}
        itemClassName="board-media-attachment"
      />
    </>
  );

  const replyMediaBusy = replyMediaItems.some((item) => MEDIA_BUSY_STATUSES.has(item.status));
  const replyMediaAttachments = replyMediaItems.map((item) => item.media).filter(Boolean);
  const replyHasContent = Boolean(boardHtmlToText(replyDraft) || replyMediaAttachments.length > 0);
  const topicMediaBusy = topicMediaItems.some((item) => MEDIA_BUSY_STATUSES.has(item.status));
  const removeTopicMediaItem = (itemId) => {
    setTopicMediaItems((current) => {
      const item = current.find((entry) => entry.id === itemId);
      if (item?.media) discardTopicDraftMedia(item.media);
      return current.filter((entry) => entry.id !== itemId);
    });
  };
  const removeReplyMediaItem = (itemId) => {
    setReplyMediaItems((current) => {
      const item = current.find((entry) => entry.id === itemId);
      if (item?.media) discardReplyDraftMedia(item.media);
      return current.filter((entry) => entry.id !== itemId);
    });
  };

  const handleReplyToReply = (reply) => {
    setEditingReplyId('');
    setEditingReplyDraft('');
    setEditingReplyMentionMetadata(EMPTY_MENTION_METADATA);
    setReplyDraft(getReplyPrefill(reply));
    setReplyComposerExpanded(true);
    replyComposerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openTopic = (topicId) => {
    setEditingTopicId('');
    setEditingTopicTitle('');
    setEditingTopicDraft('');
    setEditingTopicMentionMetadata(EMPTY_MENTION_METADATA);
    setEditingReplyId('');
    setEditingReplyDraft('');
    setEditingReplyMentionMetadata(EMPTY_MENTION_METADATA);
    setReplyDraft('');
    setReplyMentionMetadata(EMPTY_MENTION_METADATA);
    setBoardState({ topicId, panel: 'discussion' }, true);
  };

  const renderModerationDashboard = () => {
    const flaggedCount = visibleFlaggedItems.length;
    const filterLabel = reviewCaseFilter === 'closed' ? 'Closed' : reviewCaseFilter === 'all' ? 'All' : 'Active';

    return (
      <div className="board-admin-panel">
        <div className="board-admin-panel__hero">
          <div>
            <strong>{showSupportQueue ? 'Platform Review' : 'Review Queue'}</strong>
            <p className="text-muted">
              {showSupportQueue
                ? 'Cross-club safeguarding and moderation cases that need Member Nest support attention.'
                : 'Review flagged or reported board content across this club.'}
            </p>
          </div>
          <div className="board-admin-panel__stats">
            <div className="board-admin-stat">
              <span>{reviewCaseCounts.active}</span>
              <small>Active cases</small>
            </div>
            <div className="board-admin-stat">
              <span>{reviewCaseCounts.closed}</span>
              <small>Closed cases</small>
            </div>
            {!showSupportQueue && (
              <div className="board-admin-stat">
                <span>{flaggedCount}</span>
                <small>Flagged without case</small>
              </div>
            )}
          </div>
        </div>

        {!showSupportQueue && visibleFlaggedItems.length > 0 && (
          <section className="board-case-section">
            <div className="board-case-section__header">
              <div>
                <strong>Flagged in this board</strong>
                <p className="text-muted">These items are visible in discussion but do not have a review case yet.</p>
              </div>
            </div>

            <div className="board-case-list">
              {visibleFlaggedItems.map((item) => (
                <article key={item.key} className="board-case-card board-case-card--pending">
                  <div className="board-case-card__header">
                    <div>
                      <strong>{item.title}</strong>
                      <div className="text-muted board-case-card__meta">
                        <span>{item.contentType}</span>
                        <span>{formatDateTime(item.createdAt)}</span>
                      </div>
                    </div>
                    <span className="board-case-pill">{String(item.flagStatus || 'flagged').replaceAll('_', ' ')}</span>
                  </div>
                  <p className="board-case-preview">
                    {clampPreview(item.preview || 'Flagged content is visible in this board, but no review case exists yet.', 240)}
                  </p>
                  <div className="board-case-card__action-row">
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => {
                        setBoardState({ topicId: item.topicId, panel: 'discussion' }, true);
                      }}
                    >
                      Open Context
                    </button>
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      disabled={busyAction === `flagged-left_visible-${item.contentId}`}
                      onClick={() => handleReviewFlaggedItem(item, 'left_visible')}
                    >
                      Mark Reviewed
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      disabled={busyAction === `flagged-hide-${item.contentId}`}
                      onClick={() => handleReviewFlaggedItem(item, 'hide')}
                    >
                      Hide Content
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="board-case-section">
          <div className="board-case-section__header">
            <div>
              <strong>{showSupportQueue ? 'Platform cases' : `${filterLabel} review cases`}</strong>
              <p className="text-muted">
                {showSupportQueue
                  ? 'Cases across clubs that need escalation or platform-level oversight.'
                  : 'Cases already under review for this club, including closed history.'}
              </p>
            </div>
            <div className="board-review-filters" aria-label="Review case filters">
              {[
                ['active', `Active ${reviewCaseCounts.active}`],
                ['closed', showSupportQueue && reviewCaseFilter !== 'closed' ? 'Closed' : `Closed ${reviewCaseCounts.closed}`],
                ['all', showSupportQueue && reviewCaseFilter !== 'all' ? 'All' : `All ${reviewCaseCounts.all}`],
              ].map(([filterId, label]) => (
                <button
                  key={filterId}
                  type="button"
                  className={`board-topic-filter ${reviewCaseFilter === filterId ? 'active' : ''}`}
                  onClick={() => setReviewCaseFilter(filterId)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <ReviewCaseToolbar
            search={caseSearch}
            onSearchChange={setCaseSearch}
            filters={caseFilters}
            onFiltersChange={setCaseFilters}
            clubOptions={caseClubOptions}
            boardOptions={caseBoardOptions}
            showClubFilter={showSupportQueue}
            resultCount={currentCases.length}
            totalCount={usingCaseArchive ? archiveTotal : currentCaseSource.length}
            usingArchive={usingCaseArchive}
            loading={caseSearchLoading}
          />

          {caseSearchLoading ? (
            <div className="board-case-loading">Loading review cases...</div>
          ) : currentCases.length === 0 ? (
            <EmptyState
              title={caseToolsActive
                ? 'No review cases match those filters.'
                : reviewCaseFilter === 'closed'
                  ? 'No closed cases found.'
                  : `No ${filterLabel.toLowerCase()} review cases`}
              description={caseToolsActive
                ? 'Try a broader search term or remove one of the filters.'
                : showSupportQueue
                  ? 'No platform cases match this filter.'
                  : 'No club review cases match this filter.'}
            />
          ) : (
            <div className="board-case-list">
              {currentCases.map((caseEntry) => (
                <ModerationCaseCard
                  key={`${caseEntry.teamId}-${caseEntry.boardId}-${caseEntry.id}`}
                  caseEntry={caseEntry}
                  busyAction={busyAction}
                  showTeamContext={showSupportQueue}
                  reviewScope={showSupportQueue ? 'platform' : 'club'}
                  highlighted={caseEntry.id === selectedCaseId}
                  onOpenContext={() => {
                    setBoardState({
                      clubId: caseEntry.teamId,
                      teamId: caseEntry.teamId,
                      boardId: caseEntry.boardId,
                      topicId: caseEntry.topicId,
                      panel: 'discussion',
                    });
                  }}
                  onReview={handleReviewCase}
                  onContactAdmin={handleContactAdmin}
                  onOpenEvidence={handleOpenEvidence}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    );
  };

  if (teamsLoading) {
    return <div className="messages-chat"><div className="chat-placeholder">Loading boards…</div></div>;
  }

  const mobileStep = getBoardsMobileStep({ isMobile, isCompactPwaViewport, selectedTopicId, selectedBoard });
  const showBoardRail = !isMobile || mobileStep === 'browse';
  const showBoardsMain = !isMobile || mobileStep !== 'browse';
  const showTopicPane = !isMobile || mobileStep === 'topics';
  const showThreadPane = !isMobile || mobileStep === 'thread';
  const canCreateTopicInSelectedBoard = Boolean(
    selectedBoard
    && !showSupportQueue
    && !selectedBoardIsEventComments
    && canWriteSelectedBoard
  );
  const canShowBoardManagementActions = Boolean(canManageTeamBoards && canCreateTopicInSelectedBoard);
  const showModeSwitcher = Boolean(canViewModeration || isSupportAdmin);
  const showBoardHeaderControls = Boolean(showModeSwitcher || canShowBoardManagementActions);

  return (
    <>
      <BoardFormModal
        isOpen={boardModalState.open}
        board={boardModalState.board}
        adminTeams={adminTeams}
        initialTeamId={
          selectedTeam?.isAdmin
            ? selectedTeam.id
            : selectedClub?.isAdmin
              ? selectedClub.id
              : adminTeams[0]?.id || ''
        }
        teamGifSettings={selectedTeam?.communicationSettings?.gifs || selectedClub?.communicationSettings?.gifs || {}}
        onClose={() => setBoardModalState({ open: false, board: null })}
        onSave={handleSaveBoard}
      />

      <BoardDeleteDialog
        board={pendingBoardDelete}
        busy={busyAction === `delete-board-${pendingBoardDelete?.id}`}
        onClose={() => setPendingBoardDelete(null)}
        onConfirm={handleDeleteBoard}
      />

      <TopicComposerModal
        isOpen={topicModalOpen}
        boardName={selectedBoard?.name || ''}
        title={topicComposerTitle}
        value={topicComposerValue}
        error={topicComposerError}
        mediaItems={topicMediaItems}
        mediaBusy={topicMediaBusy}
        saving={busyAction === 'create-topic'}
        fileInputRef={topicFileInputRef}
        onClose={() => setTopicModalOpen(false)}
        onTitleChange={setTopicComposerTitle}
        onChange={setTopicComposerValue}
        onAttachMedia={handleAttachTopicMedia}
        onRemoveMedia={removeTopicMediaItem}
        onMentionMetadataChange={(metadata) => setTopicMentionMetadata(normalizeMentionMetadata(metadata))}
        searchMentionTargets={handleSearchMentionTargets}
        gifsEnabled={selectedBoardGifPolicy.enabled}
        gifRating={selectedBoardGifPolicy.rating}
        onSubmit={handleCreateTopic}
      />

      <EditHistoryModal
        isOpen={historyModalState.open}
        title={historyModalState.title}
        content={historyModalState.content}
        onClose={() => setHistoryModalState({ open: false, title: '', content: null })}
      />

      <ModerationEvidenceModal
        isOpen={evidenceModalState.open}
        evidence={evidenceModalState.evidence}
        loading={evidenceModalState.loading}
        error={evidenceModalState.error}
        onClose={() => setEvidenceModalState({ open: false, evidence: null, loading: false, error: '' })}
      />

      <div className={`messages-layout boards-layout boards-layout--${mobileStep}`}>
        {showBoardRail && (
        <aside className="boards-rail">
          <section className="boards-rail-card boards-rail-card--boards">
            <div className="boards-side-header">
              <div>
                <h3 className="sidebar-header">Boards</h3>
                <p className="boards-side-header__meta">Choose a board to view its topics.</p>
              </div>
              {canCreateTeamBoards && (
                <button type="button" className="btn btn--primary btn--sm" onClick={() => setBoardModalState({ open: true, board: null })}>
                  New Board
                </button>
              )}
            </div>

            <div className="board-club-filter" role="tablist" aria-label="Filter boards by club">
              <button
                type="button"
                className={`board-club-filter__chip ${!selectedClubFilter ? 'active' : ''}`}
                onClick={() => handleClubFilterChange('')}
              >
                All Clubs
              </button>
              {teams.map((team) => (
                <button
                  key={team.id}
                  type="button"
                  className={`board-club-filter__chip ${selectedClubFilter === team.id ? 'active' : ''}`}
                  onClick={() => handleClubFilterChange(team.id)}
                >
                  <span>{team.name}</span>
                  {team.isAdmin && <span className="board-club-filter__admin">Admin</span>}
                </button>
              ))}
            </div>

            {boardsLoading ? (
              <p className="text-muted">Loading boards…</p>
            ) : visibleBoards.length === 0 ? (
              <p className="text-muted">
                {selectedClub ? 'No boards yet for this club.' : 'No clubs currently have boards or archives available.'}
              </p>
            ) : (
              <div className="board-list">
                {visibleBoards.map((board) => {
                  const boardUnreadCount = Number(unreadSummary?.byBoard?.[boardNotificationKey(board.teamId, board.id)] || 0);
                  return (
                    <button
                      key={`${board.teamId}-${board.id}`}
                      type="button"
                      className={`board-list-item ${board.id === selectedBoardId && board.teamId === selectedTeamId && !showSupportQueue ? 'active' : ''}`}
                      onClick={() => setBoardState({ teamId: board.teamId, boardId: board.id, topicId: null, panel: 'discussion' })}
                    >
                      <div className="board-list-item__title-row">
                        <strong>{board.name || 'Untitled board'}</strong>
                        <span className="board-list-item__status">
                          {boardUnreadCount > 0 && (
                            <span className="message-unread-badge" aria-label={`${boardUnreadCount} unread update${boardUnreadCount === 1 ? '' : 's'} on this board`}>
                              {boardUnreadCount > 99 ? '99+' : boardUnreadCount}
                            </span>
                          )}
                          <span className="board-list-item__meta">{getBoardVisibilityLabel(board)}</span>
                        </span>
                      </div>
                      <div className="board-list-item__club">{board.teamName || 'Club'}</div>
                      <div className="text-muted board-list-item__desc">
                        {board.description || 'No description yet.'}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </aside>
        )}

        {showBoardsMain && (
        <div className="messages-chat boards-main">
          <div className="boards-main__header">
            <div className="boards-main__title-block">
              {isMobile && mobileStep === 'topics' && (
                <MobileBoardTrail
                  steps={[
                    { label: 'Boards', onClick: () => setBoardState({ boardId: null, topicId: null, panel: 'discussion' }, true) },
                    { label: selectedBoard?.name || 'Topics' },
                  ]}
                />
              )}
              {isMobile && mobileStep === 'thread' && (
                <MobileBoardTrail
                  steps={[
                    { label: 'Boards', onClick: () => setBoardState({ boardId: null, topicId: null, panel: 'discussion' }, true) },
                    { label: selectedBoard?.name || 'Topics', onClick: () => setBoardState({ topicId: null, panel: 'discussion' }, true) },
                    { label: 'Thread' },
                  ]}
                />
              )}
              <strong>{showSupportQueue ? 'Platform Review' : showModerationPanel ? 'Review Queue' : selectedBoard?.name || 'Boards'}</strong>
              {!showSupportQueue && selectedBoard && (
                <div className="boards-main__subhead text-muted">
                  <span>{selectedBoard.teamName || selectedTeam?.name || 'Club'}</span>
                  <span>{selectedBoard.description || 'No board description yet.'}</span>
                  <span>{getBoardVisibilityLabel(selectedBoard)}</span>
                  {selectedBoardReadOnly && <span>Read-only archive</span>}
                  <span>{topicItems.length} topics</span>
                </div>
              )}
            </div>

            {showBoardHeaderControls && (
            <div className="boards-main__controls">
              <ModeSwitcher
                isSupportAdmin={isSupportAdmin}
                canViewModeration={canViewModeration}
                activeMode={activeMode}
                compact={isCompactBoards}
                onChange={(mode) => {
                  const nextPanel = mode === 'support' ? 'support' : mode === 'review' ? 'review' : 'discussion';
                  setBoardState({ panel: nextPanel }, true);
                }}
              />

              {!isCompactBoards && canShowBoardManagementActions && (
                <>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setBoardModalState({ open: true, board: selectedBoard })}
                  >
                    Edit Board
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger btn--sm board-delete-action"
                    disabled={busyAction === `delete-board-${selectedBoard.id}`}
                    onClick={() => setPendingBoardDelete({
                      id: selectedBoard.id,
                      name: selectedBoard.name,
                      teamId: selectedTeamId,
                    })}
                  >
                    Delete Board
                  </button>
                </>
              )}

              {isCompactBoards && canShowBoardManagementActions && (
                <ActionMenu label="Board actions">
                  <ActionMenuItem onClick={() => setBoardModalState({ open: true, board: selectedBoard })}>
                    Edit Board
                  </ActionMenuItem>
                  <ActionMenuItem
                    disabled={busyAction === `delete-board-${selectedBoard.id}`}
                    onClick={() => setPendingBoardDelete({
                      id: selectedBoard.id,
                      name: selectedBoard.name,
                      teamId: selectedTeamId,
                    })}
                  >
                    Delete Board
                  </ActionMenuItem>
                </ActionMenu>
              )}
            </div>
            )}
          </div>

          {error && <p className="text-danger" style={{ marginBottom: '0.75rem' }}>{error}</p>}

          {showSupportQueue || showModerationPanel ? (
            renderModerationDashboard()
          ) : !selectedBoard ? (
            <EmptyState
              title="Select a board"
              description="Choose a club board from the left to browse current discussions."
            />
          ) : (
            <div className="boards-discussion-layout">
              {showTopicPane && (
              <section className="boards-thread-list-pane">
                <div className="boards-pane-header">
                  <div>
                    <strong>Topics</strong>
                    <p className="boards-pane-helper text-muted">Choose a topic to open the thread.</p>
                  </div>
                  <span className="board-section-count">{visibleTopics.length}</span>
                </div>

                {canCreateTopicInSelectedBoard && (
                  <div className="boards-topic-pane-actions">
                    <button
                      type="button"
                      className="btn btn--primary btn--sm boards-topic-pane-actions__new"
                      onClick={() => {
                        setTopicComposerError('');
                        setTopicModalOpen(true);
                      }}
                    >
                      New Topic
                    </button>
                  </div>
                )}

                <TopicToolbar
                  canViewModeration={canViewModeration}
                  filter={topicFilter}
                  onFilterChange={setTopicFilter}
                  search={topicSearch}
                  onSearchChange={setTopicSearch}
                  totalCount={topicItems.length}
                  visibleCount={visibleTopics.length}
                />

                {topicsLoading ? (
                  <TopicListSkeleton />
                ) : topicItems.length === 0 ? (
                  <EmptyState
                    title="No topics yet"
                    description={selectedBoardIsEventComments ? 'Event comment threads appear here after someone comments from an event.' : selectedBoardReadOnly ? 'This board is currently available as a read-only archive.' : 'Start the first topic to give this board a clear purpose.'}
                    actionLabel={!selectedBoardIsEventComments && canWriteSelectedBoard ? 'Start a Topic' : undefined}
                    onAction={!selectedBoardIsEventComments && canWriteSelectedBoard ? () => {
                      setTopicComposerError('');
                      setTopicModalOpen(true);
                    } : undefined}
                  />
                ) : visibleTopics.length === 0 ? (
                  <EmptyState
                    title="No topics match"
                    description="Try a different search term or clear the current filters to see more discussion."
                    actionLabel={topicToolsAreActive ? 'Clear filters' : undefined}
                    onAction={topicToolsAreActive
                      ? () => {
                        setTopicSearch('');
                        setTopicFilter('all');
                      }
                      : undefined}
                  />
                ) : (
                  <div className="board-topic-list">
                    {visibleTopics.map((topic) => (
                      <TopicListItem
                        key={topic.id}
                        topic={topic}
                        active={topic.id === selectedTopicId}
                        showFlags={canViewModeration}
                        unreadCount={Number(unreadSummary?.byTopic?.[boardNotificationKey(selectedTeamId, selectedBoardId, topic.id)] || 0)}
                        onSelect={() => openTopic(topic.id)}
                      />
                    ))}
                  </div>
                )}
              </section>
              )}

              {showThreadPane && (
              <section className={`boards-thread-detail-pane ${selectedTopicId ? 'boards-thread-detail-pane--active-topic' : ''}`}>
                {!selectedTopic ? (
                  <EmptyState
                    title={selectedTopicId && topicsLoading ? 'Loading thread' : 'Choose a topic'}
                    description={selectedTopicId && topicsLoading
                      ? 'Opening the board discussion from your notification.'
                      : 'Pick a thread from the topic list to read the conversation and reply.'}
                  />
                ) : (
                  <div className="boards-thread-detail boards-thread-detail--dense">
                    <div className="board-thread-scroll-region">
                    <article
                      ref={topicIsLatestUnreadTarget ? latestUpdateRef : null}
                      className={`board-thread-card board-thread-card--topic ${selectedTopic.pending ? 'is-pending' : ''} ${topicIsLatestUnreadTarget ? 'board-thread-card--unread-target' : ''}`}
                    >
                      <div className="board-thread-card__header">
                        <div className="board-thread-card__title-block">
                          <div className="board-thread-card__eyebrow">
                            <span>Thread</span>
                            {selectedTopic.pending && <PendingBadge label="Posting topic" />}
                            {!selectedTopic.pending && selectedTopic.isPinned && <StateBadge label="Pinned for all" tone="global" />}
                            {!selectedTopic.pending && !selectedTopic.isPinned && selectedTopic.isPersonalPinned && <StateBadge label="Pinned for me" tone="personal" />}
                            {!selectedTopic.pending && selectedTopic.replyStatus === 'locked' && <StateBadge label="Locked" tone="neutral" />}
                            {!selectedTopic.pending && canViewModeration && <FlagBadge content={selectedTopic} />}
                          </div>
                          <strong className="board-thread-card__title">{getTopicTitle(selectedTopic)}</strong>
                          <div className="board-thread-meta">
                            <span>Posted by {selectedTopic.authorName || 'Member'}</span>
                            <span>{formatRelevantDateTime(selectedTopic.createdAt)}</span>
                            <span>{selectedTopic.replyCount || 0} replies</span>
                          </div>
                        </div>

                        <div className="board-thread-card__actions">
                          {!selectedTopic.pending && canWriteSelectedBoard && selectedTopic.replyStatus !== 'locked' && (
                            <IconActionButton label="Reply" onClick={() => {
                              setReplyDraft('');
                              setReplyComposerExpanded(true);
                              replyComposerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}>
                              ↩︎
                            </IconActionButton>
                          )}
                          {!selectedTopic.pending && (
                            <PinActionMenu
                              topic={selectedTopic}
                              canPinForAll={canManageTeamBoards && canWriteSelectedBoard && !selectedBoardIsEventComments}
                              savingPersonal={savingTopicId === selectedTopic.id}
                              savingGlobal={pinningTopicId === selectedTopic.id}
                              onTogglePersonal={() => togglePersonalPin(selectedTopic.id)}
                              onToggleGlobal={() => toggleGlobalPin(selectedTopic)}
                            />
                          )}
                          {!selectedTopic.pending && (
                            (canWriteSelectedBoard && selectedTopic.authorId === user?.uid && selectedTopic.moderationStatus !== 'hidden')
                            || (Array.isArray(selectedTopic.editHistory) && selectedTopic.editHistory.length > 0)
                            || (canWriteSelectedBoard && selectedTopic.authorId !== user?.uid)
                            || (canWriteSelectedBoard && canManageTeamBoards)
                            || (canViewModeration && selectedTopic.flagStatus && selectedTopic.flagStatus !== 'clear')
                          ) && (
                            <ActionMenu label="Topic actions">
                              {canWriteSelectedBoard && selectedTopic.authorId === user?.uid && selectedTopic.moderationStatus !== 'hidden' && (
                                <ActionMenuItem
                                  onClick={() => {
                                    setEditingTopicId(selectedTopic.id);
                                    setEditingTopicTitle(selectedTopic.title || getTopicTitle(selectedTopic));
                                    setEditingTopicDraft(selectedTopic.bodyHtml || '');
                                    setEditingTopicMentionMetadata(EMPTY_MENTION_METADATA);
                                  }}
                                >
                                  Edit Topic
                                </ActionMenuItem>
                              )}
                              {canWriteSelectedBoard && selectedTopic.authorId === user?.uid && selectedTopic.moderationStatus !== 'deleted' && (
                                <ActionMenuItem
                                  disabled={busyAction === `self-delete-topic-${selectedTopic.id}`}
                                  onClick={() => handleDeleteMyContent({
                                    contentType: 'topic',
                                    contentId: selectedTopic.id,
                                    topicId: selectedTopic.id,
                                  })}
                                >
                                  Delete Topic
                                </ActionMenuItem>
                              )}
                              {Array.isArray(selectedTopic.editHistory) && selectedTopic.editHistory.length > 0 && (
                                <ActionMenuItem
                                  onClick={() => setHistoryModalState({
                                    open: true,
                                    title: 'Topic Edit History',
                                    content: selectedTopic,
                                  })}
                                >
                                  Edit History
                                </ActionMenuItem>
                              )}
                              {canWriteSelectedBoard && canManageTeamBoards && (
                                <>
                                  <ActionMenuItem
                                    disabled={busyAction === `topic-${selectedTopic.replyStatus === 'locked' ? 'unlock_topic' : 'lock_topic'}-${selectedTopic.id}`}
                                    onClick={() => handleTopicModerationAction(
                                      selectedTopic,
                                      selectedTopic.replyStatus === 'locked' ? 'unlock_topic' : 'lock_topic'
                                    )}
                                  >
                                    {selectedTopic.replyStatus === 'locked' ? 'Unlock Topic' : 'Lock Topic'}
                                  </ActionMenuItem>
                                  <ActionMenuItem
                                    disabled={busyAction === `topic-hide-${selectedTopic.id}`}
                                    onClick={() => handleTopicModerationAction(selectedTopic, 'hide')}
                                  >
                                    Hide Topic
                                  </ActionMenuItem>
                                  <ActionMenuItem
                                    disabled={busyAction === `topic-delete_content-${selectedTopic.id}`}
                                    onClick={() => handleTopicModerationAction(selectedTopic, 'delete_content')}
                                  >
                                    Delete Topic
                                  </ActionMenuItem>
                                </>
                              )}
                              {canWriteSelectedBoard && selectedTopic.authorId !== user?.uid && (
                                <ActionMenuItem
                                  onClick={() => handleReportContent({
                                    contentType: 'topic',
                                    contentId: selectedTopic.id,
                                    topicId: selectedTopic.id,
                                  })}
                                >
                                  Flag for Review
                                </ActionMenuItem>
                              )}
                              {canViewModeration && selectedTopic.flagStatus && selectedTopic.flagStatus !== 'clear' && (
                                <ActionMenuItem onClick={() => setBoardState({ panel: 'review' }, true)}>
                                  Open Review
                                </ActionMenuItem>
                              )}
                            </ActionMenu>
                          )}
                        </div>
                      </div>

                      {editingTopicId === selectedTopic.id ? (
                        <>
                          {!selectedBoardIsEventComments && (
                            <label className="form-label board-topic-edit-title">
                              Topic title
                              <input
                                type="text"
                                className="form-input"
                                value={editingTopicTitle}
                                onChange={(event) => setEditingTopicTitle(event.target.value)}
                                maxLength={120}
                                disabled={busyAction === 'update-topic'}
                              />
                            </label>
                          )}
                          <RichTextEditor
                            value={editingTopicDraft}
                            onChange={setEditingTopicDraft}
                            onMentionMetadataChange={(metadata) => setEditingTopicMentionMetadata(normalizeMentionMetadata(metadata))}
                            searchMentionTargets={handleSearchMentionTargets}
                            gifsEnabled={selectedBoardGifPolicy.enabled}
                            gifRating={selectedBoardGifPolicy.rating}
                            minHeight={180}
                          />
                          <div className="board-inline-actions">
                            <button
                              type="button"
                              className="btn btn--primary btn--sm"
                              onClick={handleUpdateTopic}
                              disabled={busyAction === 'update-topic'}
                            >
                              {busyAction === 'update-topic' ? 'Saving…' : 'Save Topic'}
                            </button>
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => {
                                setEditingTopicId('');
                                setEditingTopicTitle('');
                                setEditingTopicDraft('');
                                setEditingTopicMentionMetadata(EMPTY_MENTION_METADATA);
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        renderRichContent(selectedTopic)
                      )}
                      {editingTopicId !== selectedTopic.id && (
                        <BoardReactionBar
                          teamId={selectedTeamId}
                          boardId={selectedBoardId}
                          topicId={selectedTopic.id}
                          contentType="topic"
                          contentId={selectedTopic.id}
                          content={selectedTopic}
                          currentUserId={user?.uid || ''}
                          canReact={canWriteSelectedBoard}
                        />
                      )}
                    </article>

                    <section className="board-thread-stream">
                      <div className="board-thread-stream__summary">
                        <div>
                          <strong>{`${threadReplies.length} ${threadReplies.length === 1 ? 'reply' : 'replies'}`}</strong>
                          <p className="text-muted">Keep replies specific so the thread stays easy to follow.</p>
                          {selectedTopicUnreadCount > 0 && (
                            <button
                              type="button"
                              className="board-unread-link"
                              onClick={handleJumpToLatestUnread}
                              disabled={!latestReplyId && !topicIsLatestUnreadTarget}
                            >
                              <span>{selectedTopicUnreadCount > 99 ? '99+' : selectedTopicUnreadCount}</span>
                              Jump to unread
                            </button>
                          )}
                        </div>
                        <div className="board-reply-sort">
                          {busyAction === 'create-reply' && <PendingBadge label="Sending" />}
                          <div className="board-reply-sort__control" aria-label="Reply order">
                            <button
                              type="button"
                              className={`board-reply-sort__option ${replySortOrder === 'oldest' ? 'active' : ''}`}
                              onClick={() => setReplySortOrder('oldest')}
                            >
                              Oldest
                            </button>
                            <button
                              type="button"
                              className={`board-reply-sort__option ${replySortOrder === 'newest' ? 'active' : ''}`}
                              onClick={() => setReplySortOrder('newest')}
                            >
                              Newest
                            </button>
                          </div>
                        </div>
                      </div>

                      {threadReplies.length === 0 ? (
                        <div className="board-thread-stream__empty">
                          <strong>No replies yet</strong>
                          <p className="text-muted">Be the first to move the discussion forward.</p>
                        </div>
                      ) : (
                        <div className="board-thread-stream__list">
                          {threadReplies.map((reply) => (
                            <article
                              key={reply.id}
                              ref={reply.id === latestReplyId ? latestUpdateRef : null}
                              className={`board-thread-card board-thread-card--reply ${reply.pending ? 'is-pending' : ''} ${selectedTopicUnreadCount > 0 && reply.id === latestReplyId ? 'board-thread-card--unread-target' : ''}`}
                            >
                              <div className="board-reply-card__body-row">
                                <div className="board-reply-card__content">
                                  {editingReplyId === reply.id ? (
                                    <>
                                      <RichTextEditor
                                        value={editingReplyDraft}
                                        onChange={setEditingReplyDraft}
                                        onMentionMetadataChange={(metadata) => setEditingReplyMentionMetadata(normalizeMentionMetadata(metadata))}
                                        searchMentionTargets={handleSearchMentionTargets}
                                        gifsEnabled={selectedBoardGifPolicy.enabled}
                                        gifRating={selectedBoardGifPolicy.rating}
                                        minHeight={140}
                                      />
                                      <div className="board-inline-actions">
                                        <button
                                          type="button"
                                          className="btn btn--primary btn--sm"
                                          onClick={() => handleUpdateReply(selectedTopic.id)}
                                          disabled={busyAction === 'update-reply'}
                                        >
                                          {busyAction === 'update-reply' ? 'Saving…' : 'Save Reply'}
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn--ghost btn--sm"
                                          onClick={() => {
                                            setEditingReplyId('');
                                            setEditingReplyDraft('');
                                            setEditingReplyMentionMetadata(EMPTY_MENTION_METADATA);
                                          }}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </>
                                  ) : (
                                    renderRichContent(reply)
                                  )}
                                </div>
                                <div className="board-thread-card__actions">
                                  {!reply.pending && canWriteSelectedBoard && (
                                    <IconActionButton label="Reply" onClick={() => handleReplyToReply(reply)}>
                                      ↩︎
                                    </IconActionButton>
                                  )}
                                  {!reply.pending && (
                                    (canWriteSelectedBoard && reply.authorId === user?.uid && reply.moderationStatus !== 'hidden')
                                    || (Array.isArray(reply.editHistory) && reply.editHistory.length > 0)
                                    || (canWriteSelectedBoard && reply.authorId !== user?.uid)
                                    || (canViewModeration && reply.flagStatus && reply.flagStatus !== 'clear')
                                  ) && (
                                    <ActionMenu label="Reply actions">
                                      {canWriteSelectedBoard && reply.authorId === user?.uid && reply.moderationStatus !== 'hidden' && (
                                        <ActionMenuItem
                                          onClick={() => {
                                            setEditingReplyId(reply.id);
                                            setEditingReplyDraft(reply.bodyHtml || '');
                                            setEditingReplyMentionMetadata(EMPTY_MENTION_METADATA);
                                          }}
                                        >
                                          Edit Reply
                                        </ActionMenuItem>
                                      )}
                                      {canWriteSelectedBoard && reply.authorId === user?.uid && reply.moderationStatus !== 'deleted' && (
                                        <ActionMenuItem
                                          disabled={busyAction === `self-delete-reply-${reply.id}`}
                                          onClick={() => handleDeleteMyContent({
                                            contentType: 'reply',
                                            contentId: reply.id,
                                            topicId: selectedTopic.id,
                                          })}
                                        >
                                          Delete Reply
                                        </ActionMenuItem>
                                      )}
                                      {Array.isArray(reply.editHistory) && reply.editHistory.length > 0 && (
                                        <ActionMenuItem
                                          onClick={() => setHistoryModalState({
                                            open: true,
                                            title: 'Reply Edit History',
                                            content: reply,
                                          })}
                                        >
                                          Edit History
                                        </ActionMenuItem>
                                      )}
                                      {canWriteSelectedBoard && reply.authorId !== user?.uid && (
                                        <ActionMenuItem
                                          onClick={() => handleReportContent({
                                            contentType: 'reply',
                                            contentId: reply.id,
                                            topicId: selectedTopic.id,
                                          })}
                                        >
                                          Flag for Review
                                        </ActionMenuItem>
                                      )}
                                      {canViewModeration && reply.flagStatus && reply.flagStatus !== 'clear' && (
                                        <ActionMenuItem onClick={() => setBoardState({ panel: 'review' }, true)}>
                                          Open Review
                                        </ActionMenuItem>
                                      )}
                                    </ActionMenu>
                                  )}
                                </div>
                              </div>
                              <div className="board-reply-card__meta">
                                <span>{reply.authorName || 'Member'}</span>
                                <span>{formatRelevantDateTime(reply.createdAt)}</span>
                                {reply.pending && <PendingBadge label="Posting reply" />}
                                {!reply.pending && canViewModeration && <FlagBadge content={reply} />}
                              </div>
                              {editingReplyId !== reply.id && (
                                <BoardReactionBar
                                  teamId={selectedTeamId}
                                  boardId={selectedBoardId}
                                  topicId={selectedTopic.id}
                                  contentType="reply"
                                  contentId={reply.id}
                                  content={reply}
                                  currentUserId={user?.uid || ''}
                                  canReact={canWriteSelectedBoard}
                                />
                              )}
                            </article>
                          ))}
                        </div>
                      )}
                    </section>
                    </div>

                    {selectedBoardReadOnly ? (
                      <section ref={replyComposerRef} className="board-compose-card board-compose-card--inline">
                        <div className="board-compose-card__header">
                          <div>
                            <strong>This board is read-only</strong>
                            <p className="text-muted">
                              Existing discussions are available as an archive while posting is disabled.
                            </p>
                          </div>
                        </div>
                      </section>
                    ) : selectedTopic.replyStatus === 'locked' ? (
                      <section ref={replyComposerRef} className="board-compose-card board-compose-card--inline">
                        <div className="board-compose-card__header">
                          <div>
                            <strong>This topic is locked</strong>
                            <p className="text-muted">
                              Replies are disabled until a club admin unlocks the topic.
                            </p>
                          </div>
                        </div>
                      </section>
                    ) : (
                      <section
                        ref={replyComposerRef}
                        className={`board-compose-card board-compose-card--inline ${replyComposerExpanded ? 'board-compose-card--reply-composer' : ''}`}
                      >
                        {!replyComposerExpanded ? (
                          <button
                            type="button"
                            className="board-reply-prompt"
                            onClick={() => setReplyComposerExpanded(true)}
                          >
                            {replyHasContent ? 'Continue your reply' : 'Add your reply'}
                          </button>
                        ) : (
                          <>
                            <div className="board-compose-card__header">
                              <div>
                                <strong>Reply to this topic</strong>
                                <p className="text-muted">
                                  Keep it concise, constructive, and easy to act on.
                                </p>
                              </div>
                              <button
                                type="button"
                                className="board-compose-card__collapse"
                                onClick={() => {
                                  setReplyComposerExpanded(false);
                                  setReplyComposerPopoutOpen(false);
                                }}
                              >
                                Collapse
                              </button>
                            </div>
                            <RichTextEditor
                              value={replyDraft}
                              onChange={setReplyDraft}
                              onMentionMetadataChange={(metadata) => setReplyMentionMetadata(normalizeMentionMetadata(metadata))}
                              searchMentionTargets={handleSearchMentionTargets}
                              gifsEnabled={selectedBoardGifPolicy.enabled}
                              gifRating={selectedBoardGifPolicy.rating}
                              minHeight={170}
                              placeholder="Add a reply..."
                            />
                            {replyMediaItems.length > 0 && (
                              <div className="board-media-composer">
                                <ComposerMediaRail
                                  items={replyMediaItems}
                                  onRemove={removeReplyMediaItem}
                                  removeButtonClassName="board-media-composer__remove"
                                  busyStatuses={MEDIA_BUSY_STATUSES}
                                  statusDetail={mediaDraftStatusDetail}
                                />
                              </div>
                            )}
                            <div className="board-compose-card__footer board-compose-card__footer--actions">
                              <div className="text-muted board-compose-card__hint">
                                Replies appear in order beneath the original topic.
                              </div>
                              <div className="board-compose-actions">
                                <div className="board-compose-actions__tools">
                                  <input
                                    ref={replyFileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/webm"
                                    multiple
                                    onChange={handleAttachReplyMedia}
                                    hidden
                                  />
                                  <button
                                    type="button"
                                    className="board-compose-actions__tool"
                                    onClick={() => replyFileInputRef.current?.click()}
                                    disabled={busyAction === 'create-reply' || selectedTopic.pending || replyMediaBusy}
                                    aria-label="Attach photo or video"
                                    title="Attach photo or video"
                                  >
                                    <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21.44 11.05 12 20.49a6 6 0 0 1-8.49-8.49l9.44-9.44a4 4 0 0 1 5.66 5.66l-9.44 9.44a2 2 0 0 1-2.83-2.83l8.49-8.49" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    className="board-compose-actions__tool"
                                    onClick={() => setReplyComposerPopoutOpen(true)}
                                    aria-label="Open larger composer"
                                    title="Open larger composer"
                                  >
                                    <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M15 3h6v6" />
                                      <path d="M10 14 21 3" />
                                      <path d="M9 21H3v-6" />
                                      <path d="M14 10 3 21" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    className="board-compose-actions__tool"
                                    onClick={() => {
                                      setReplyDraft('');
                                      discardReplyDraftMediaItems(replyMediaItems);
                                      setReplyMediaItems([]);
                                      setReplyMentionMetadata(EMPTY_MENTION_METADATA);
                                      setReplyComposerExpanded(false);
                                    }}
                                    disabled={!replyHasContent || replyMediaBusy}
                                    aria-label="Clear reply draft"
                                    title="Clear reply draft"
                                  >
                                    <svg aria-hidden="true" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M3 6h18" />
                                      <path d="M8 6V4h8v2" />
                                      <path d="M19 6l-1 14H6L5 6" />
                                      <path d="M10 11v5" />
                                      <path d="M14 11v5" />
                                    </svg>
                                  </button>
                                </div>
                                <button
                                  type="button"
                                  className="btn btn--primary btn--sm board-compose-actions__send"
                                  onClick={() => handleCreateReply(selectedTopic.id)}
                                  disabled={busyAction === 'create-reply' || selectedTopic.pending || replyMediaBusy || !replyHasContent}
                                >
                                  {busyAction === 'create-reply' ? 'Posting…' : 'Post Reply'}
                                </button>
                              </div>
                            </div>
                            {replyComposerPopoutOpen && typeof document !== 'undefined' && createPortal(
                              <div className="board-compose-popout" role="presentation" onMouseDown={() => setReplyComposerPopoutOpen(false)}>
                                <div className="board-compose-popout__panel" role="dialog" aria-modal="true" aria-label="Compose board reply" onMouseDown={(event) => event.stopPropagation()}>
                                  <div className="board-compose-popout__header">
                                    <h2>Compose reply</h2>
                                    <button type="button" onClick={() => setReplyComposerPopoutOpen(false)} aria-label="Close composer">×</button>
                                  </div>
                                  <RichTextEditor
                                    value={replyDraft}
                                    onChange={setReplyDraft}
                                    onMentionMetadataChange={(metadata) => setReplyMentionMetadata(normalizeMentionMetadata(metadata))}
                                    searchMentionTargets={handleSearchMentionTargets}
                                    gifsEnabled={selectedBoardGifPolicy.enabled}
                                    gifRating={selectedBoardGifPolicy.rating}
                                    minHeight={360}
                                    placeholder="Add a reply..."
                                  />
                                  <div className="board-compose-popout__footer">
                                    <span>Write the full reply here, then post when ready.</span>
                                    <div className="board-compose-popout__actions">
                                      <button
                                        type="button"
                                        className="btn btn--ghost btn--sm"
                                        onClick={() => {
                                          setReplyDraft('');
                                          discardReplyDraftMediaItems(replyMediaItems);
                                          setReplyMediaItems([]);
                                          setReplyMentionMetadata(EMPTY_MENTION_METADATA);
                                        }}
                                        disabled={!replyHasContent || replyMediaBusy}
                                      >
                                        Clear
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn--primary btn--sm"
                                        onClick={() => handleCreateReply(selectedTopic.id)}
                                        disabled={busyAction === 'create-reply' || selectedTopic.pending || replyMediaBusy || !replyHasContent}
                                      >
                                        {busyAction === 'create-reply' ? 'Posting…' : 'Post Reply'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>,
                              document.body
                            )}
                          </>
                        )}
                      </section>
                    )}
                  </div>
                )}
              </section>
              )}
            </div>
          )}
        </div>
        )}
      </div>
    </>
  );
}
